import React, { useState, ChangeEvent, FormEvent, useEffect, useRef } from 'react';
import InputField from '../common/InputField';
import Button from '../common/Button';
import SideDrawer from '../common/SideDrawer';
import { handleKeyDown, validateFieldWithSchema, focusNextFormField } from '../../utils/util';
import { set, z } from "zod";
import { InputType, IsFieldValid } from '../../shared/interfaces';
import type { AutoCompleteOption, PartyCode, Party } from '../../shared/interfaces';
import { PlusIcon, TrashIcon } from '@heroicons/react/24/solid';
import apiService from '../../services/ApiService';
import { apiRoutes } from '../../shared/apiRoutes';

interface PartyFormData {
  partyCode: string;
  partyName: string;
  city: string;
  remarks: string;
  phone: string;
  openingBalance: string;
  vatav: string;
  harCut: string;
  partners: Partner[];
  partnerCommission: string;
  partnerBCommission: string;
  thirdPartyCode: string;
  sessionPartyCommission: string;
  sessionThirdPartyCommission: string;
  oneDayPartyCommission: string;
  oneDayThirdPartyCommission: string;
  testPartyCommission: string;
  testThirdPartyCommission: string;
  thirdPartyHarCut: string;
  hideInGeneral: boolean;
  [key: string]: any; // Add index signature to allow string keys
}

interface PartnerDirtyFields {
  partnerCode: boolean,
  partnerCommission: boolean
}

interface PartnerIsValid {
  partnerCode: IsFieldValid,
  partnerCommission: IsFieldValid
}

interface Partner {
  partnerCode: string,
  partnerCommission: number,
  [key: string]: any; // Add index signature to allow string keys 
}

interface PartyFormProps {
  isOpen: boolean;
  onClose: (value: boolean) => void;
  onSaveSuccess?: () => void; // Optional callback to refresh parent list
  id?: number; // Optional id for editing existing party
}

/** -999.99 … 999.99 while typing; strips invalid chars, one decimal, ≤2 fractional digits */
function sanitizeCommissionInput(raw: string): string {
  if (raw === '' || raw === '-') return raw;
  let neg = false;
  let s = raw;
  if (s.startsWith('-')) {
    neg = true;
    s = s.slice(1);
  }
  s = s.replace(/[^0-9.]/g, '');
  const firstDot = s.indexOf('.');
  if (firstDot === -1) {
    return neg ? '-' + s : s;
  }
  const intPart = s.slice(0, firstDot).replace(/\./g, '');
  const rest = s.slice(firstDot + 1).replace(/\./g, '');
  const fracPart = rest.slice(0, 2);
  return neg ? '-' + intPart + '.' + fracPart : intPart + '.' + fracPart;
}

function normalizeHarCutFromApi(data: { harCut?: unknown; HarCut?: unknown }): string {
  const h = data.harCut ?? data.HarCut;
  return h != null && h !== '' ? String(h) : '0';
}

function normalizeThirdPartyHarCutFromApi(data: { thirdPartyHarCut?: unknown; ThirdPartyHarCut?: unknown }): string {
  const h = data.thirdPartyHarCut ?? data.ThirdPartyHarCut;
  return h != null && h !== '' ? String(h) : '0';
}

function normalizeHideInGeneralFromApi(data: { hideInGeneral?: unknown; HideInGeneral?: unknown }): boolean {
  const h = data.hideInGeneral ?? data.HideInGeneral;
  return h === true || h === 'true';
}

const CreatePartyDialog: React.FC<PartyFormProps> = ({isOpen, onClose, onSaveSuccess, id}) => {
  const MAX_PARTNERS_ALLOWED = 4;
  const [isProcessing, setIsProcessing] = useState<boolean | undefined>(false);
  
  const initialFormData: PartyFormData = {
    partyCode: '',
    partyName: '',
    city: '',
    remarks: '',
    phone: '',
    openingBalance: '0',
    vatav: '0',
    harCut: '0',
    partners: [{
      partnerCode: "",
      partnerCommission: 0,
    }],
    partnerCommission: '0',
    partnerBCommission: '0',
    thirdPartyCode: '',
    sessionPartyCommission: '0',
    sessionThirdPartyCommission: '0',
    oneDayPartyCommission: '0',
    oneDayThirdPartyCommission: '0',
    testPartyCommission: '0',
    testThirdPartyCommission: '0',
    thirdPartyHarCut: '0',
    hideInGeneral: false,
  };
  
  const [formData, setFormData] = useState<PartyFormData>(initialFormData);


  interface FieldsBoolean {
    partyCode: boolean,
    partyName: boolean,
    city: boolean,
    remarks: boolean,
    phone: boolean,
    openingBalance: boolean,
    vatav: boolean,
    harCut: boolean,
    partners: PartnerDirtyFields[],
    partnerCommission: boolean,
    partnerBCode: boolean,
    partnerBCommission: boolean,
    thirdPartyCode: boolean,
    sessionPartyCommission: boolean,
    sessionThirdPartyCommission: boolean,
    oneDayPartyCommission: boolean,
    oneDayThirdPartyCommission: boolean,
    testPartyCommission: boolean,
    testThirdPartyCommission: boolean,
    harCutThirdPartyCode: boolean,
    thirdPartyHarCut: boolean,
    [key: string]: any; // Add index signature to allow string keys
  }

  interface FieldsIsValid {
    partyCode: IsFieldValid,
    partyName: IsFieldValid,
    city: IsFieldValid,
    remarks: IsFieldValid,
    phone: IsFieldValid,
    openingBalance: IsFieldValid,
    vatav: IsFieldValid,
    harCut: IsFieldValid,
    partners: PartnerIsValid[],
    partnerCommission: IsFieldValid,
    partnerBCode: IsFieldValid,
    partnerBCommission: IsFieldValid,
    thirdPartyCode: IsFieldValid,
    sessionPartyCommission: IsFieldValid,
    sessionThirdPartyCommission: IsFieldValid,
    oneDayPartyCommission: IsFieldValid,
    oneDayThirdPartyCommission: IsFieldValid,
    testPartyCommission: IsFieldValid,
    testThirdPartyCommission: IsFieldValid,
    harCutThirdPartyCode: IsFieldValid,
    thirdPartyHarCut: IsFieldValid,
    [key: string]: any; // Add index signature to allow string keys
  }

  // Add state for partyCodes
  const [partyCodes, setPartyCodes] = useState<AutoCompleteOption[]>([]);
  // Track raw input values for commission fields to handle "-" while typing
  const [commissionInputValues, setCommissionInputValues] = useState<Record<string, string>>({});
  const [dirtyFields, setDirtyFields] = useState<FieldsBoolean>({
    partyCode: false,
    partyName: false,
    city: false,
    remarks: false,
    phone: false,
    openingBalance: false,
    vatav: false,
    harCut: false,
    partners: [{
      partnerCode: false,
      partnerCommission: false,
    }],
    partnerCommission: false,
    partnerBCode: false,
    partnerBCommission: false,
    thirdPartyCode: false,
    sessionPartyCommission: false,
    sessionThirdPartyCommission: false,
    oneDayPartyCommission: false,
    oneDayThirdPartyCommission: false,
    testPartyCommission: false,
    testThirdPartyCommission: false,
    harCutThirdPartyCode: false,
    thirdPartyHarCut: false,
  });

  const [selectedPartyCode, setSelectedPartyCode] = useState<AutoCompleteOption | null>({ id: 0, name: '' });
  const [selectedPartnerCode, setSelectedPartnerCode] = useState<AutoCompleteOption[]  | null>([{ id: 0, name: '' }]);
  
  // Third Party Code state (similar to LagayaKhadha party code)
  const thirdPartyCodeInputRef = useRef<HTMLInputElement>(null);
  // Ref for Save Party button
  const savePartyButtonRef = useRef<HTMLButtonElement>(null);
  // Ref for Save & Continue button
  const saveAndContinueButtonRef = useRef<HTMLButtonElement>(null);
  const [thirdPartyCode, setThirdPartyCode] = useState<string>(''); // Current typed value
  const [validatedThirdPartyCode, setValidatedThirdPartyCode] = useState<string>(''); // Original validated code
  const [thirdPartyValidation, setThirdPartyValidation] = useState<{ isValid: boolean; error: string; partyName: string; partyId: number | null }>({
    isValid: false,
    error: '',
    partyName: '',
    partyId: null
  });

  const harCutThirdPartyCodeInputRef = useRef<HTMLInputElement>(null);
  const [harCutThirdPartyCode, setHarCutThirdPartyCode] = useState<string>('');
  const [validatedHarCutThirdPartyCode, setValidatedHarCutThirdPartyCode] = useState<string>('');
  const [harCutThirdPartyValidation, setHarCutThirdPartyValidation] = useState<{ isValid: boolean; error: string; partyName: string; partyId: number | null }>({
    isValid: false,
    error: '',
    partyName: '',
    partyId: null
  });

  // Partner Code state (similar to LagayaKhadha party code) - array for multiple partners
  const partnerCodeInputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const [partnerCodes, setPartnerCodesState] = useState<string[]>(['']); // Current typed values
  const [validatedPartnerCodes, setValidatedPartnerCodes] = useState<string[]>(['']); // Original validated codes
  const [partnerValidations, setPartnerValidations] = useState<Array<{ isValid: boolean; error: string; partyName: string; partyId: number | null }>>([{
    isValid: false,
    error: '',
    partyName: '',
    partyId: null
  }]);

  const [isFieldValid, setIsFieldValid] = useState<FieldsIsValid>(
    {
      partyCode: { isValid: true, message: "" },
      partyName: { isValid: true, message: "" },
      city: { isValid: true, message: "" },
      remarks: { isValid: true, message: "" },
      phone: { isValid: true, message: "" },
      openingBalance: { isValid: true, message: "" },
      vatav: { isValid: true, message: "" },
      harCut: { isValid: true, message: "" },
      partners: [{
        partnerCode: { isValid: true, message: "" },
        partnerCommission: { isValid: true, message: "" },
      }],
      partnerCommission: { isValid: true, message: "" },
      partnerBCode: { isValid: true, message: "" },
      partnerBCommission: { isValid: true, message: "" },
      thirdPartyCode: { isValid: true, message: "" },
      sessionPartyCommission: { isValid: true, message: "" },
      sessionThirdPartyCommission: { isValid: true, message: "" },
      oneDayPartyCommission: { isValid: true, message: "" },
      oneDayThirdPartyCommission: { isValid: true, message: "" },
      testPartyCommission: { isValid: true, message: "" },
      testThirdPartyCommission: { isValid: true, message: "" },
      harCutThirdPartyCode: { isValid: true, message: "" },
      thirdPartyHarCut: { isValid: true, message: "" }
    }
  );

  const handleAddPartner = () => {
    setFormData((prev) => (
      {
        ...prev,
        partners: [
          ...(prev.partners || []),
          {
            partnerCode: "",
            partnerCommission: 0,
          },
        ]
      }

    ));
    setDirtyFields((prev) => ({
      ...prev,
      partners: [
        ...(prev.partners || []),
        {
          partnerCode: false,
          partnerCommission: false,
        },
      ]
    }));

    setIsFieldValid((prev) => ({
      ...prev,
      partners: [
        ...(prev.partners || []),
        {
          partnerCode: { isValid: true, message: "" },
          partnerCommission: { isValid: true, message: "" },
        },
      ]
    }));

    // Initialize partner code state for new partner
    setPartnerCodesState(prev => [...prev, '']);
    setValidatedPartnerCodes(prev => [...prev, '']);
    setPartnerValidations(prev => [...prev, {
      isValid: false,
      error: '',
      partyName: '',
      partyId: null
    }]);
  };

  const handleDeletePartner = (indexToRemove: number) => {
    setFormData((prev) => ({
      ...prev,
      partners: prev.partners.filter((_, index) => index !== indexToRemove),
    }));
    setDirtyFields((prev) => {
      const updatedPartners = [...prev.partners];
      updatedPartners.splice(indexToRemove, 1);
      return {
        ...prev,
        partners: updatedPartners
      };
    });

    setIsFieldValid((prev) => {
      const updatedPartners = [...prev.partners];
      updatedPartners.splice(indexToRemove, 1);
      return {
        ...prev,
        partners: updatedPartners
      };
    });
    setSelectedPartnerCode((prev) => {
      const updated = [...(prev || [])];
      updated.splice(indexToRemove, 1);
      return updated;
    });
    // Remove partner code state
    setPartnerCodesState(prev => prev.filter((_, index) => index !== indexToRemove));
    setValidatedPartnerCodes(prev => prev.filter((_, index) => index !== indexToRemove));
    setPartnerValidations(prev => prev.filter((_, index) => index !== indexToRemove));
    partnerCodeInputRefs.current = partnerCodeInputRefs.current.filter((_, index) => index !== indexToRemove);
  };

  const partyCodeValidation = z
    .string({
      required_error: "Party code is required"
    })
    .min(1, "Party code must be 1 characters")
    .max(5, "Party code must be 5 characters")
    .regex(/^[a-zA-Z0-9]+$/, "Party code must be alphanumeric");

  const partnerPartyCode = z
    .string()
    .max(5, "Partner Code must be at most 5 characters")
    .refine(val => val.length == 0 || val.length >= 1, {
      message: "Partner Code must be at least 1 characters if entered",
    }).optional();

  /** Commission fields: -999 to 999, up to 2 decimal places */
  const commissionStringSchema = z.string().refine(
    (val) => {
      if (val === '' || val === '-') return true;
      const n = Number(val);
      if (Number.isNaN(n)) return false;
      if (n < -999 || n > 999) return false;
      const dot = val.indexOf('.');
      if (dot === -1) return true;
      return val.slice(dot + 1).length <= 2;
    },
    { message: 'Commission must be between -999 and 999 with up to 2 decimal places' }
  );

  const partyFormSchema = z.object({
    partyCode: partyCodeValidation,
    partyName: z.string({ required_error: "Party name is required" }).min(1, "Party name must be at least 1 character").max(60),
    city: z.string()
      .max(30, "City must be at most 30 characters")
      .refine(val => val.length == 0 || val.length >= 3, {
        message: "City must be at least 3 characters if entered",
      })
      .optional(),
    remarks: z.string().max(255),
    phone: z
      .string()
      .optional()
      .refine((val) => (val ?? '').length === 0 || /^\d{10}$/.test(val as string), {
        message: "Must be 10 digits",
      }),
    openingBalance: z.coerce.number().min(-99999999999).max(99999999999),
    vatav: z.coerce.number().min(0).max(99),
    harCut: z.coerce.number().min(0).max(99999),
    thirdPartyHarCut: z.coerce.number().min(0).max(99999),
    thirdPartyCode: partnerPartyCode,
    partnerCommission: commissionStringSchema,
    partnerBCommission: commissionStringSchema,
    sessionPartyCommission: commissionStringSchema,
    sessionThirdPartyCommission: commissionStringSchema,
    oneDayPartyCommission: commissionStringSchema,
    oneDayThirdPartyCommission: commissionStringSchema,
    testPartyCommission: commissionStringSchema,
    testThirdPartyCommission: commissionStringSchema,
  });

  const partenerSchema = z.object({
    partnerCode: partnerPartyCode,
    partnerCommission: z.preprocess(
      (val) => (typeof val === 'number' ? String(val) : val),
      commissionStringSchema
    ),
  });



  // Validate partner code format (same as LagayaKhadha)
  const validatePartnerCodeFormat = (code: string): { isValid: boolean; error: string } => {
    if (!code || code.trim().length === 0) {
      return { isValid: true, error: '' }; // Optional field
    }
    if (code.length < 1) {
      return { isValid: false, error: 'Party code must be at least 1 character' };
    }
    if (code.length > 5) {
      return { isValid: false, error: 'Party code must be at most 5 characters' };
    }
    if (!/^[a-zA-Z0-9]+$/.test(code)) {
      return { isValid: false, error: 'Party code must be alphanumeric' };
    }
    return { isValid: true, error: '' };
  };

  // Validate and fetch partner party by code (similar to LagayaKhadha)
  const validateAndFetchPartnerParty = async (code: string, index: number): Promise<{ isValid: boolean; error: string; partyName: string; partyId: number | null }> => {
    // Extract code part if user pasted "CODE - NAME" format
    const codeMatch = code.match(/^([A-Z0-9]+)(\s*-\s*.*)?$/i);
    const trimmedCode = codeMatch ? codeMatch[1].toUpperCase().trim() : code.trim().toUpperCase();
    
    // First validate format
    const formatValidation = validatePartnerCodeFormat(trimmedCode);
    if (!formatValidation.isValid) {
      const validationResult = {
        isValid: false,
        error: formatValidation.error,
        partyName: '',
        partyId: null
      };
      setPartnerValidations(prev => {
        const updated = [...prev];
        updated[index] = validationResult;
        return updated;
      });
      setValidatedPartnerCodes(prev => {
        const updated = [...prev];
        updated[index] = '';
        return updated;
      });
      setIsFieldValid(prev => ({
        ...prev,
        partners: prev.partners.map((p, i) => i === index ? { ...p, partnerCode: { isValid: false, message: formatValidation.error } } : p)
      }));
      return validationResult;
    }

    // If empty, allow it (optional field)
    if (!trimmedCode) {
      const validationResult = {
        isValid: true,
        error: '',
        partyName: '',
        partyId: null
      };
      setPartnerValidations(prev => {
        const updated = [...prev];
        updated[index] = validationResult;
        return updated;
      });
      setValidatedPartnerCodes(prev => {
        const updated = [...prev];
        updated[index] = '';
        return updated;
      });
      setPartnerCodesState(prev => {
        const updated = [...prev];
        updated[index] = '';
        return updated;
      });
      if (partnerCodeInputRefs.current[index]) {
        partnerCodeInputRefs.current[index]!.value = '';
      }
      setIsFieldValid(prev => ({
        ...prev,
        partners: prev.partners.map((p, i) => i === index ? { ...p, partnerCode: { isValid: true, message: '' } } : p)
      }));
      return validationResult;
    }

    // Check if party exists in local partyCodes list first
    const foundParty = partyCodes.find(pc => (pc.code || '').toUpperCase() === trimmedCode);
    
    if (foundParty) {
      const validationResult = {
        isValid: true,
        error: '',
        partyName: foundParty.name?.replace(/^[^-]+-\s*/, '') || '',
        partyId: foundParty.id as number
      };
      setPartnerValidations(prev => {
        const updated = [...prev];
        updated[index] = validationResult;
        return updated;
      });
      setValidatedPartnerCodes(prev => {
        const updated = [...prev];
        updated[index] = trimmedCode;
        return updated;
      });
      // Update input to show "CODE - NAME"
      if (partnerCodeInputRefs.current[index]) {
        partnerCodeInputRefs.current[index]!.value = foundParty.name || foundParty.code || '';
      }
      setPartnerCodesState(prev => {
        const updated = [...prev];
        updated[index] = trimmedCode;
        return updated;
      });
      setIsFieldValid(prev => ({
        ...prev,
        partners: prev.partners.map((p, i) => i === index ? { ...p, partnerCode: { isValid: true, message: '' } } : p)
      }));
      return validationResult;
    }

    // If not found locally, try to fetch from API
    try {
      const partyData = await apiService.get(apiRoutes.parties.getByCode(trimmedCode));
      if (partyData && partyData.id && partyData.id !== 1) {
        const validationResult = {
          isValid: true,
          error: '',
          partyName: partyData.partyName || '',
          partyId: partyData.id
        };
        setPartnerValidations(prev => {
          const updated = [...prev];
          updated[index] = validationResult;
          return updated;
        });
        setValidatedPartnerCodes(prev => {
          const updated = [...prev];
          updated[index] = trimmedCode;
          return updated;
        });
        // Update input to show "CODE - NAME"
        if (partnerCodeInputRefs.current[index]) {
          partnerCodeInputRefs.current[index]!.value = `${partyData.partyCode} - ${partyData.partyName}`;
        }
        setPartnerCodesState(prev => {
          const updated = [...prev];
          updated[index] = trimmedCode;
          return updated;
        });
        setIsFieldValid(prev => ({
          ...prev,
          partners: prev.partners.map((p, i) => i === index ? { ...p, partnerCode: { isValid: true, message: '' } } : p)
        }));
        return validationResult;
      } else {
        const validationResult = {
          isValid: false,
          error: 'Invalid party code',
          partyName: '',
          partyId: null
        };
        setPartnerValidations(prev => {
          const updated = [...prev];
          updated[index] = validationResult;
          return updated;
        });
        setValidatedPartnerCodes(prev => {
          const updated = [...prev];
          updated[index] = '';
          return updated;
        });
        setIsFieldValid(prev => ({
          ...prev,
          partners: prev.partners.map((p, i) => i === index ? { ...p, partnerCode: { isValid: false, message: 'Invalid party code' } } : p)
        }));
        return validationResult;
      }
    } catch (error) {
      const validationResult = {
        isValid: false,
        error: 'Invalid party code',
        partyName: '',
        partyId: null
      };
      setPartnerValidations(prev => {
        const updated = [...prev];
        updated[index] = validationResult;
        return updated;
      });
      setValidatedPartnerCodes(prev => {
        const updated = [...prev];
        updated[index] = '';
        return updated;
      });
      setIsFieldValid(prev => ({
        ...prev,
        partners: prev.partners.map((p, i) => i === index ? { ...p, partnerCode: { isValid: false, message: 'Invalid party code' } } : p)
      }));
      return validationResult;
    }
  };

  // Handle partner code input change
  const handlePartnerCodeChange = (e: React.ChangeEvent<HTMLInputElement>, index: number) => {
    const value = e.target.value;
    
    // Extract code part if user pasted "CODE - NAME" format, otherwise use the typed value
    const codeMatch = value.match(/^([A-Z0-9]+)(\s*-\s*.*)?$/i);
    const codeOnly = codeMatch ? codeMatch[1].toUpperCase() : value.toUpperCase();
    
    // Check if user is typing a new value (different from validated code)
    const isNewValue = codeOnly !== validatedPartnerCodes[index];
    
    if (isNewValue) {
      // User is typing a new value, clear validation and show only the code
      setPartnerCodesState(prev => {
        const updated = [...prev];
        updated[index] = codeOnly;
        return updated;
      });
      setValidatedPartnerCodes(prev => {
        const updated = [...prev];
        updated[index] = '';
        return updated;
      });
      setPartnerValidations(prev => {
        const updated = [...prev];
        updated[index] = {
          isValid: false,
          error: '',
          partyName: '',
          partyId: null
        };
        return updated;
      });
      // Update input to show only the code (remove "CODE - NAME" format if present)
      if (partnerCodeInputRefs.current[index] && value !== codeOnly) {
        partnerCodeInputRefs.current[index]!.value = codeOnly;
      }
      setIsFieldValid(prev => ({
        ...prev,
        partners: prev.partners.map((p, i) => i === index ? { ...p, partnerCode: { isValid: true, message: '' } } : p)
      }));
    } else {
      // User hasn't changed the validated code, keep the display format
      setPartnerCodesState(prev => {
        const updated = [...prev];
        updated[index] = codeOnly;
        return updated;
      });
    }
  };

  // Handle partner code blur
  const handlePartnerCodeBlur = async (index: number) => {
    const inputRef = partnerCodeInputRefs.current[index];
    const value = inputRef?.value || partnerCodes[index] || '';
    // Extract code part if value contains "CODE - NAME" format
    const codeMatch = value.match(/^([A-Z0-9]+)(\s*-\s*.*)?$/i);
    const codeToValidate = codeMatch ? codeMatch[1].toUpperCase().trim() : value.trim().toUpperCase();
    
    if (codeToValidate) {
      // Only validate if it's a new value (different from validated code)
      if (codeToValidate !== validatedPartnerCodes[index]) {
        await validateAndFetchPartnerParty(codeToValidate, index);
      }
    } else {
      // Empty value - clear validation (optional field)
      setPartnerValidations(prev => {
        const updated = [...prev];
        updated[index] = {
          isValid: true,
          error: '',
          partyName: '',
          partyId: null
        };
        return updated;
      });
      setValidatedPartnerCodes(prev => {
        const updated = [...prev];
        updated[index] = '';
        return updated;
      });
      setPartnerCodesState(prev => {
        const updated = [...prev];
        updated[index] = '';
        return updated;
      });
      if (inputRef) {
        inputRef.value = '';
      }
      setIsFieldValid(prev => ({
        ...prev,
        partners: prev.partners.map((p, i) => i === index ? { ...p, partnerCode: { isValid: true, message: '' } } : p)
      }));
    }
  };

  // Handle partner code Enter key (similar to LagayaKhadha and Havala)
  const handlePartnerCodeEnter = (e: React.KeyboardEvent<HTMLInputElement>, index: number) => {
    if (!e.currentTarget) return;
    const currentTarget = e.currentTarget;
    const inputRef = partnerCodeInputRefs.current[index];
    const value = inputRef?.value || partnerCodes[index] || '';
    const codeMatch = value.match(/^([A-Z0-9]+)(\s*-\s*.*)?$/i);
    const codeToValidate = codeMatch ? codeMatch[1].toUpperCase().trim() : value.trim().toUpperCase();

    if (codeToValidate && codeToValidate !== validatedPartnerCodes[index]) {
      validateAndFetchPartnerParty(codeToValidate, index).catch(() => {});
    }

    setTimeout(() => {
      if (!currentTarget?.form) return;
      focusNextFormField(currentTarget, currentTarget.form);
    }, 50);
  };

  function getPartnerFieldMeta(e: ChangeEvent<HTMLInputElement>) {
    let index = -1;
    let innerName = '';
    const { name, value, type, checked } = e.target;
    if (name.startsWith("partnerCode")) {
      innerName = 'partnerCode';
      index = parseInt(name.replace("partnerCode", "")) - 1;
    } else if (name.startsWith("partnerCommission")) {
      innerName = 'partnerCommission';
      index = parseInt(name.replace("partnerCommission", "")) - 1;
    }
    return { name, value, type, checked, index, innerName };
  }

  const handleBlur = (e: ChangeEvent<HTMLInputElement>) => {
    const { name, value, index, innerName } = getPartnerFieldMeta(e);
    setDirtyFields((prev: FieldsBoolean) => {
      if (index < 0) {
        return {
          ...prev,
          [name]: true
        };
      } else if (index >= 0 && innerName) {
        const updatedPartners = [...prev.partners];
        updatedPartners[index] = {
          ...updatedPartners[index],
          [innerName]: true
        };
        return {
          ...prev,
          partners: updatedPartners
        };
      }
      return prev;
    });
  }

  const isPartyCodeDuplicate = (partyCode: string): boolean => {
    // Check if partyCode is already in the list    
    return partyCodes.some((option) => option.name.toLowerCase() === partyCode.toLowerCase());
  }

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked, index, innerName } = getPartnerFieldMeta(e);

    if (type === 'checkbox' && index < 0) {
      setFormData((prev) => ({
        ...prev,
        [name]: checked,
      }));
      setDirtyFields((prev: FieldsBoolean) => ({
        ...prev,
        [name]: true,
      }));
      return;
    }

    // Ensure commission fields allow negative numbers
    const isCommissionField = name.includes('Commission') || name.includes('commission');
    let processedValue = value;
    if (isCommissionField) {
      if (value === '' || value === '-') {
        processedValue = value;
      } else {
        processedValue = sanitizeCommissionInput(value);
      }
    }
    if (name === 'harCut') {
      processedValue = value.replace(/\D/g, '').slice(0, 5);
      if (processedValue === '') {
        processedValue = '0';
      }
    }

    setFormData((prev) => {
      if (index < 0) {
        return {
          ...prev,
          [name]: processedValue
        };
      } else if (index >= 0 && innerName) {
        const updatedPartners = [...prev.partners];
        // For partnerCommission, store as number but track raw input for display
        if (innerName === 'partnerCommission') {
          const fieldKey = `partnerCommission${index + 1}`;
          // Store raw input value for display
          setCommissionInputValues(prev => ({
            ...prev,
            [fieldKey]: processedValue
          }));
          
          // Store numeric value in formData
          let numValue = 0;
          if (processedValue !== '' && processedValue !== '-') {
            const parsed = Number(processedValue);
            numValue = isNaN(parsed) ? 0 : parsed;
          }
          updatedPartners[index] = {
            ...updatedPartners[index],
            partnerCommission: numValue
          };
        } else {
          updatedPartners[index] = {
            ...updatedPartners[index],
            [innerName]: processedValue
          };
        }
        return {
          ...prev,
          partners: updatedPartners
        };
      }
      
      // Track raw input values for non-partner commission fields
      if (isCommissionField && index < 0) {
        setCommissionInputValues(prev => ({
          ...prev,
          [name]: processedValue
        }));
      }
      return prev;
    }
    );

    // Mark the field as dirty
    setDirtyFields((prev: FieldsBoolean) => {
      if (index < 0) {
        return {
          ...prev,
          [name]: true
        };
      } else if (index >= 0 && innerName) {
        const updatedPartners = [...prev.partners];
        updatedPartners[index] = {
          ...updatedPartners[index],
          [innerName]: true
        };
        return {
          ...prev,
          partners: updatedPartners
        };
      }
      return prev;
    })

    // Validate the field using zod and update isFieldValid accordingly
    setIsFieldValid((prev: FieldsIsValid) => {
      let isValid: boolean, message: string;
      
      let validationValue = name === 'harCut' || name === 'thirdPartyHarCut' ? processedValue : value;
      if (isCommissionField && (value === '-' || value === '')) {
        isValid = true;
        message = '';
      } else {
        if (isCommissionField) {
          validationValue = (processedValue === '' || processedValue === '-') ? '0' : processedValue;
        }
        
        if (index >= 0 && innerName) {
          ({ isValid, message } = validateFieldWithSchema(
            innerName,
            validationValue,
            formData['partners'][index],
            partenerSchema
          ));
        } else {
          ({ isValid, message } = validateFieldWithSchema(
            name,
            validationValue,
            formData,
            partyFormSchema
          ));
        }
      }
      if(name === 'partyCode') {
        const isDuplicate = isPartyCodeDuplicate(value);
        if(isDuplicate) {
          isValid = false;
          message = "Party code already exists";
        }
      }
      if (index < 0) {
        return {
          ...prev,
          [name]: { isValid, message }
        };
      } else if (index >= 0 && innerName) {
        const updatedPartners = [...prev.partners];
        updatedPartners[index] = {
          ...updatedPartners[index],
          [innerName]: { isValid, message }
        };
        return {
          ...prev,
          partners: updatedPartners
        };
      }
      return prev;
    });

  }

  /**
   * Iterates through all fields in formData and sets isFieldValid for each field using the provided schemas.
   */
  const validateAllFields = (formData: PartyFormData) => {
    // Validate main fields
    const mainResult = partyFormSchema.safeParse(formData);
    let mainFieldValid: Partial<FieldsIsValid> = {};

    if (!mainResult.success) {
      for (const issue of mainResult.error.issues) {
        mainFieldValid[issue.path[0] as string] = {
          isValid: false,
          message: issue.message,
        };
      }
    }

    // Validate partners
    const partnersValid: PartnerIsValid[] = formData.partners.map((partner) => {
      const result = partenerSchema.safeParse(partner);
      let partnerValid: PartnerIsValid = {
        partnerCode: { isValid: true, message: "" },
        partnerCommission: { isValid: true, message: "" },
      };
      if (!result.success) {
        for (const issue of result.error.issues) {
          partnerValid[issue.path[0] as keyof PartnerIsValid] = {
            isValid: false,
            message: issue.message,
          };
        }
      }
      return partnerValid;
    });

    setIsFieldValid((prev) => ({
      ...prev,
      ...mainFieldValid,
      partners: partnersValid,
    }));
  };

  const handlePartnerCodeSelecte = (selected: AutoCompleteOption, index: number) => {
    setSelectedPartnerCode((prev) => {
      const updated = [...(prev || [])];
      updated[index] = selected;
      return updated;
    });

    // Extract party code from selected option (use code field or extract from name)
    const partyCode = selected.code || selected.name.split(' - ')[0] || selected.name;

    // Update formData with the selected partner code
    setFormData((prev) => {
      const updatedPartners = [...prev.partners];
      updatedPartners[index].partnerCode = partyCode;
      return {
        ...prev,
        partners: updatedPartners
      };
    });

    // Validate the partner code field
    setIsFieldValid((prev) => {
      const updatedPartners = [...prev.partners];
      updatedPartners[index].partnerCode = { isValid: true, message: "" };
      return {
        ...prev,
        partners: updatedPartners
      };
    });
  }


  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();

    // Validate main form fields
    const mainValidation = partyFormSchema.safeParse(formData);

    // Validate all partners  
    const partnersValidation = formData.partners.every((partner) => {
      const result = partenerSchema.safeParse(partner);
      return result.success;
    });

    if (!mainValidation.success || !partnersValidation) {
      validateAllFields(formData);
      // Mark all fields as dirty to show errors
      setDirtyFields((prev) => ({
        ...prev,
        partyCode: true,
        partyName: true,
        city: true,
        remarks: true,
        phone: true,
        openingBalance: true,
        vatav: true,
        harCut: true,
        partners: prev.partners.map(() => ({
          partnerCode: true,
          partnerCommission: true,
        })),
        partnerCommission: true,
        partnerBCode: true,
        partnerBCommission: true,
        thirdPartyCode: true,
        sessionPartyCommission: true,
        sessionThirdPartyCommission: true,
        oneDayPartyCommission: true,
        oneDayThirdPartyCommission: true,
        testPartyCommission: true,
        testThirdPartyCommission: true,
      }));
      return;
    }

    setIsProcessing(true);
    
    // Prepare the request payload
    // Convert partyCode, partyName, and city to uppercase before submitting
    const requestPayload = {
      ...(id && { id: Number(id) }), // Include id only when editing
      partyCode: formData.partyCode?.toUpperCase().trim() || '',
      partyName: formData.partyName?.toUpperCase().trim() || '',
      city: formData.city?.toUpperCase().trim() || '',
      remarks: formData.remarks,
      phone: formData.phone,
      openingBalance: Number(formData.openingBalance),
      vatav: Number(formData.vatav),
      harCut: Number(formData.harCut),
      harCutThirdPartyId: (harCutThirdPartyValidation.partyId && harCutThirdPartyValidation.partyId !== 0) ? harCutThirdPartyValidation.partyId : null,
      thirdPartyHarCut: Number(formData.thirdPartyHarCut ?? '0'),
      partners: formData.partners
        .map((p, index) => {
          const partnerValidation = partnerValidations[index];
          // Only include partners with valid party codes
          if (!partnerValidation?.isValid || !partnerValidation?.partyId) {
            return null;
          }
          // Use commissionInputValues for partnerCommission if available (handles "-" case)
          const partnerCommissionKey = `partnerCommission${index + 1}`;
          const partnerCommissionValue = commissionInputValues[partnerCommissionKey] !== undefined 
            ? commissionInputValues[partnerCommissionKey] 
            : p.partnerCommission.toString();
          return {
            partnerId: partnerValidation.partyId,
            partnerCommission: Number(partnerCommissionValue || '0'),
          };
        })
        .filter((p): p is { partnerId: number; partnerCommission: number } => p !== null),
      thirdPartyId: (thirdPartyValidation.partyId && thirdPartyValidation.partyId !== 0) ? thirdPartyValidation.partyId : null,
      // Use commissionInputValues if available (handles "-" case), otherwise use formData
      sessionPartyCommission: Number(commissionInputValues['sessionPartyCommission'] !== undefined ? commissionInputValues['sessionPartyCommission'] : formData.sessionPartyCommission || '0'),
      sessionThirdPartyCommission: Number(commissionInputValues['sessionThirdPartyCommission'] !== undefined ? commissionInputValues['sessionThirdPartyCommission'] : formData.sessionThirdPartyCommission || '0'),
      oneDayPartyCommission: Number(commissionInputValues['oneDayPartyCommission'] !== undefined ? commissionInputValues['oneDayPartyCommission'] : formData.oneDayPartyCommission || '0'),
      oneDayThirdPartyCommission: Number(commissionInputValues['oneDayThirdPartyCommission'] !== undefined ? commissionInputValues['oneDayThirdPartyCommission'] : formData.oneDayThirdPartyCommission || '0'),
      testPartyCommission: Number(commissionInputValues['testPartyCommission'] !== undefined ? commissionInputValues['testPartyCommission'] : formData.testPartyCommission || '0'),
      testThirdPartyCommission: Number(commissionInputValues['testThirdPartyCommission'] !== undefined ? commissionInputValues['testThirdPartyCommission'] : formData.testThirdPartyCommission || '0'),
      hideInGeneral: formData.hideInGeneral ?? false,
    };

    // Use PUT for updates, POST for creates
    const apiCall = id 
      ? apiService.put(`/party/${id}`, requestPayload)
      : apiService.post('/party', requestPayload);

    apiCall
      .then(async res => {
        setIsProcessing(false);
        onClose(false); // Close the dialog after successful submission
        // Call the refresh callback to update the parent list
        if (onSaveSuccess) {
          onSaveSuccess();
        }
        // Show appropriate success message
        const successMessage = id ? 'Party updated successfully!' : 'Party created successfully!';
        window.dispatchEvent(new CustomEvent('show-toast', { detail: { message: successMessage, type: 'success' } }));
        // Dispatch event to refresh party lists (for GameBoard autocomplete, etc.) when creating new party
        if (!id) {
          window.dispatchEvent(new CustomEvent('party-created'));
        }
      })
      .catch(err => {
        setIsProcessing(false);
        // Extract error message from API response
        // API returns { message: "..." } or error.response.data
        const apiErrorMessage = err?.message || err?.error || err?.response?.data?.message;
        const defaultErrorMessage = id ? 'Failed to update party' : 'Failed to create party';
        const errorMessage = apiErrorMessage || defaultErrorMessage;
        
        // Show error toast with the actual API error message
        window.dispatchEvent(new CustomEvent('show-toast', { 
          detail: { message: apiErrorMessage || `Error: ${defaultErrorMessage}`, type: 'error' } 
        }));
        
        // Don't throw error - it's already handled by showing the toast
        // This prevents uncaught runtime errors
      });
  };

  // Handle Save & Continue - saves party and resets form without closing
  const handleSaveAndContinue = async (e?: React.MouseEvent<HTMLButtonElement>) => {
    if (e) {
      e.preventDefault();
    }

    // Only allow in add mode (not edit mode)
    if (id) {
      return;
    }

    // Validate main form fields
    const mainValidation = partyFormSchema.safeParse(formData);

    // Validate all partners  
    const partnersValidation = formData.partners.every((partner) => {
      const result = partenerSchema.safeParse(partner);
      return result.success;
    });

    if (!mainValidation.success || !partnersValidation) {
      validateAllFields(formData);
      // Mark all fields as dirty to show errors
      setDirtyFields((prev) => ({
        ...prev,
        partyCode: true,
        partyName: true,
        city: true,
        remarks: true,
        phone: true,
        openingBalance: true,
        vatav: true,
        harCut: true,
        partners: prev.partners.map(() => ({
          partnerCode: true,
          partnerCommission: true,
        })),
        partnerCommission: true,
        partnerBCode: true,
        partnerBCommission: true,
        thirdPartyCode: true,
        sessionPartyCommission: true,
        sessionThirdPartyCommission: true,
        oneDayPartyCommission: true,
        oneDayThirdPartyCommission: true,
        testPartyCommission: true,
        testThirdPartyCommission: true,
        harCutThirdPartyCode: true,
        thirdPartyHarCut: true,
      }));
      return;
    }

    setIsProcessing(true);
    
    // Prepare the request payload
    const requestPayload = {
      partyCode: formData.partyCode?.toUpperCase().trim() || '',
      partyName: formData.partyName?.toUpperCase().trim() || '',
      city: formData.city?.toUpperCase().trim() || '',
      remarks: formData.remarks,
      phone: formData.phone,
      openingBalance: Number(formData.openingBalance),
      vatav: Number(formData.vatav),
      harCut: Number(formData.harCut),
      harCutThirdPartyId: (harCutThirdPartyValidation.partyId && harCutThirdPartyValidation.partyId !== 0) ? harCutThirdPartyValidation.partyId : null,
      thirdPartyHarCut: Number(formData.thirdPartyHarCut ?? '0'),
      partners: formData.partners
        .map((p, index) => {
          const partnerValidation = partnerValidations[index];
          // Only include partners with valid party codes
          if (!partnerValidation?.isValid || !partnerValidation?.partyId) {
            return null;
          }
          const partnerCommissionKey = `partnerCommission${index + 1}`;
          const partnerCommissionValue = commissionInputValues[partnerCommissionKey] !== undefined 
            ? commissionInputValues[partnerCommissionKey] 
            : p.partnerCommission.toString();
          return {
            partnerId: partnerValidation.partyId,
            partnerCommission: Number(partnerCommissionValue || '0'),
          };
        })
        .filter((p): p is { partnerId: number; partnerCommission: number } => p !== null),
      thirdPartyId: (thirdPartyValidation.partyId && thirdPartyValidation.partyId !== 0) ? thirdPartyValidation.partyId : null,
      sessionPartyCommission: Number(commissionInputValues['sessionPartyCommission'] !== undefined ? commissionInputValues['sessionPartyCommission'] : formData.sessionPartyCommission || '0'),
      sessionThirdPartyCommission: Number(commissionInputValues['sessionThirdPartyCommission'] !== undefined ? commissionInputValues['sessionThirdPartyCommission'] : formData.sessionThirdPartyCommission || '0'),
      oneDayPartyCommission: Number(commissionInputValues['oneDayPartyCommission'] !== undefined ? commissionInputValues['oneDayPartyCommission'] : formData.oneDayPartyCommission || '0'),
      oneDayThirdPartyCommission: Number(commissionInputValues['oneDayThirdPartyCommission'] !== undefined ? commissionInputValues['oneDayThirdPartyCommission'] : formData.oneDayThirdPartyCommission || '0'),
      testPartyCommission: Number(commissionInputValues['testPartyCommission'] !== undefined ? commissionInputValues['testPartyCommission'] : formData.testPartyCommission || '0'),
      testThirdPartyCommission: Number(commissionInputValues['testThirdPartyCommission'] !== undefined ? commissionInputValues['testThirdPartyCommission'] : formData.testThirdPartyCommission || '0'),
      hideInGeneral: formData.hideInGeneral ?? false,
    };

    try {
      await apiService.post('/party', requestPayload);
      
      setIsProcessing(false);
      
      // Show success message
      window.dispatchEvent(new CustomEvent('show-toast', { 
        detail: { message: 'Party created successfully!', type: 'success' } 
      }));
      
      // Dispatch event to refresh party lists
      window.dispatchEvent(new CustomEvent('party-created'));
      await fetchPartyCodes();
      
      // Call the refresh callback to update the parent list
      if (onSaveSuccess) {
        onSaveSuccess();
      }
      
      // Reset form and all state
      setFormData(initialFormData);
      setDirtyFields({
        partyCode: false,
        partyName: false,
        city: false,
        remarks: false,
        phone: false,
        openingBalance: false,
        vatav: false,
        harCut: false,
        partners: [{
          partnerCode: false,
          partnerCommission: false,
        }],
        partnerCommission: false,
        partnerBCode: false,
        partnerBCommission: false,
        thirdPartyCode: false,
        sessionPartyCommission: false,
        sessionThirdPartyCommission: false,
        oneDayPartyCommission: false,
        oneDayThirdPartyCommission: false,
        testPartyCommission: false,
        testThirdPartyCommission: false,
        harCutThirdPartyCode: false,
        thirdPartyHarCut: false,
      });
      setIsFieldValid({
        partyCode: { isValid: true, message: "" },
        partyName: { isValid: true, message: "" },
        city: { isValid: true, message: "" },
        remarks: { isValid: true, message: "" },
        phone: { isValid: true, message: "" },
        openingBalance: { isValid: true, message: "" },
        vatav: { isValid: true, message: "" },
        harCut: { isValid: true, message: "" },
        partners: [{
          partnerCode: { isValid: true, message: "" },
          partnerCommission: { isValid: true, message: "" },
        }],
        partnerCommission: { isValid: true, message: "" },
        partnerBCode: { isValid: true, message: "" },
        partnerBCommission: { isValid: true, message: "" },
        thirdPartyCode: { isValid: true, message: "" },
        sessionPartyCommission: { isValid: true, message: "" },
        sessionThirdPartyCommission: { isValid: true, message: "" },
        oneDayPartyCommission: { isValid: true, message: "" },
        oneDayThirdPartyCommission: { isValid: true, message: "" },
        testPartyCommission: { isValid: true, message: "" },
        testThirdPartyCommission: { isValid: true, message: "" },
        harCutThirdPartyCode: { isValid: true, message: "" },
        thirdPartyHarCut: { isValid: true, message: "" },
      });
      setCommissionInputValues({});
      setThirdPartyCode('');
      setValidatedThirdPartyCode('');
      setThirdPartyValidation({
        isValid: false,
        error: '',
        partyName: '',
        partyId: null
      });
      setHarCutThirdPartyCode('');
      setValidatedHarCutThirdPartyCode('');
      setHarCutThirdPartyValidation({
        isValid: false,
        error: '',
        partyName: '',
        partyId: null
      });
      if (harCutThirdPartyCodeInputRef.current) {
        harCutThirdPartyCodeInputRef.current.value = '';
      }
      setPartnerCodesState(['']);
      setValidatedPartnerCodes(['']);
      setPartnerValidations([{
        isValid: false,
        error: '',
        partyName: '',
        partyId: null
      }]);
      setSelectedPartnerCode([{ id: 0, name: '' }]);
      
      // Clear third party input ref
      if (thirdPartyCodeInputRef.current) {
        thirdPartyCodeInputRef.current.value = '';
      }
      
      // Clear partner code input refs
      partnerCodeInputRefs.current.forEach(ref => {
        if (ref) {
          ref.value = '';
        }
      });
      
      // Focus on first field (Party Code)
      setTimeout(() => {
        const partyCodeInput = document.getElementById('partyCode') as HTMLInputElement;
        partyCodeInput?.focus();
      }, 100);
      
    } catch (err: any) {
      setIsProcessing(false);
      // Extract error message from API response
      const apiErrorMessage = err?.message || err?.error || err?.response?.data?.message;
      const errorMessage = apiErrorMessage || 'Failed to create party';
      
      // Show error toast
      window.dispatchEvent(new CustomEvent('show-toast', { 
        detail: { message: errorMessage, type: 'error' } 
      }));
    }
  };

  // Validate third party code format (same as LagayaKhadha)
  const validateThirdPartyCodeFormat = (code: string): { isValid: boolean; error: string } => {
    if (!code || code.trim().length === 0) {
      return { isValid: true, error: '' }; // Optional field
    }
    if (code.length < 1) {
      return { isValid: false, error: 'Party code must be at least 1 character' };
    }
    if (code.length > 5) {
      return { isValid: false, error: 'Party code must be at most 5 characters' };
    }
    if (!/^[a-zA-Z0-9]+$/.test(code)) {
      return { isValid: false, error: 'Party code must be alphanumeric' };
    }
    return { isValid: true, error: '' };
  };

  // Validate and fetch third party by code (similar to LagayaKhadha)
  const validateAndFetchThirdParty = async (code: string): Promise<{ isValid: boolean; error: string; partyName: string; partyId: number | null }> => {
    // Extract code part if user pasted "CODE - NAME" format
    const codeMatch = code.match(/^([A-Z0-9]+)(\s*-\s*.*)?$/i);
    const trimmedCode = codeMatch ? codeMatch[1].toUpperCase().trim() : code.trim().toUpperCase();
    
    // First validate format
    const formatValidation = validateThirdPartyCodeFormat(trimmedCode);
    if (!formatValidation.isValid) {
      const validationResult = {
        isValid: false,
        error: formatValidation.error,
        partyName: '',
        partyId: null
      };
      setThirdPartyValidation(validationResult);
      setValidatedThirdPartyCode('');
      setIsFieldValid(prev => ({
        ...prev,
        thirdPartyCode: { isValid: false, message: formatValidation.error }
      }));
      return validationResult;
    }

    // If empty, allow it (optional field)
    if (!trimmedCode) {
      const validationResult = {
        isValid: true,
        error: '',
        partyName: '',
        partyId: null
      };
      setThirdPartyValidation(validationResult);
      setValidatedThirdPartyCode('');
      if (thirdPartyCodeInputRef.current) {
        thirdPartyCodeInputRef.current.value = '';
      }
      setThirdPartyCode('');
      setIsFieldValid(prev => ({
        ...prev,
        thirdPartyCode: { isValid: true, message: '' }
      }));
      return validationResult;
    }

    // Check if party exists in local partyCodes list first
    const foundParty = partyCodes.find(pc => (pc.code || '').toUpperCase() === trimmedCode);
    
    if (foundParty) {
      const validationResult = {
        isValid: true,
        error: '',
        partyName: foundParty.name?.replace(/^[^-]+-\s*/, '') || '', // Extract name part
        partyId: foundParty.id as number
      };
      setThirdPartyValidation(validationResult);
      setValidatedThirdPartyCode(trimmedCode);
      // Update input to show "CODE - NAME"
      if (thirdPartyCodeInputRef.current) {
        thirdPartyCodeInputRef.current.value = foundParty.name || foundParty.code || '';
      }
      setThirdPartyCode(trimmedCode);
      setIsFieldValid(prev => ({
        ...prev,
        thirdPartyCode: { isValid: true, message: '' }
      }));
      return validationResult;
    }

    // If not found locally, try to fetch from API
    try {
      const partyData = await apiService.get(apiRoutes.parties.getByCode(trimmedCode));
      if (partyData && partyData.id && partyData.id !== 1) {
        const validationResult = {
          isValid: true,
          error: '',
          partyName: partyData.partyName || '',
          partyId: partyData.id
        };
        setThirdPartyValidation(validationResult);
        setValidatedThirdPartyCode(trimmedCode);
        // Update input to show "CODE - NAME"
        if (thirdPartyCodeInputRef.current) {
          thirdPartyCodeInputRef.current.value = `${partyData.partyCode} - ${partyData.partyName}`;
        }
        setThirdPartyCode(trimmedCode);
        setIsFieldValid(prev => ({
          ...prev,
          thirdPartyCode: { isValid: true, message: '' }
        }));
        return validationResult;
      } else {
        const validationResult = {
          isValid: false,
          error: 'Invalid party code',
          partyName: '',
          partyId: null
        };
        setThirdPartyValidation(validationResult);
        setValidatedThirdPartyCode('');
        setIsFieldValid(prev => ({
          ...prev,
          thirdPartyCode: { isValid: false, message: 'Invalid party code' }
        }));
        return validationResult;
      }
    } catch (error) {
      const validationResult = {
        isValid: false,
        error: 'Invalid party code',
        partyName: '',
        partyId: null
      };
      setThirdPartyValidation(validationResult);
      setValidatedThirdPartyCode('');
      setIsFieldValid(prev => ({
        ...prev,
        thirdPartyCode: { isValid: false, message: 'Invalid party code' }
      }));
      return validationResult;
    }
  };

  // Validate and fetch HarCut third party by code (same rules as commission Third Party Code)
  const validateAndFetchHarCutThirdParty = async (code: string): Promise<{ isValid: boolean; error: string; partyName: string; partyId: number | null }> => {
    const codeMatch = code.match(/^([A-Z0-9]+)(\s*-\s*.*)?$/i);
    const trimmedCode = codeMatch ? codeMatch[1].toUpperCase().trim() : code.trim().toUpperCase();

    const formatValidation = validateThirdPartyCodeFormat(trimmedCode);
    if (!formatValidation.isValid) {
      const validationResult = {
        isValid: false,
        error: formatValidation.error,
        partyName: '',
        partyId: null
      };
      setHarCutThirdPartyValidation(validationResult);
      setValidatedHarCutThirdPartyCode('');
      setIsFieldValid(prev => ({
        ...prev,
        harCutThirdPartyCode: { isValid: false, message: formatValidation.error }
      }));
      return validationResult;
    }

    if (!trimmedCode) {
      const validationResult = {
        isValid: true,
        error: '',
        partyName: '',
        partyId: null
      };
      setHarCutThirdPartyValidation(validationResult);
      setValidatedHarCutThirdPartyCode('');
      if (harCutThirdPartyCodeInputRef.current) {
        harCutThirdPartyCodeInputRef.current.value = '';
      }
      setHarCutThirdPartyCode('');
      setIsFieldValid(prev => ({
        ...prev,
        harCutThirdPartyCode: { isValid: true, message: '' }
      }));
      return validationResult;
    }

    const foundParty = partyCodes.find(pc => (pc.code || '').toUpperCase() === trimmedCode);

    if (foundParty) {
      const validationResult = {
        isValid: true,
        error: '',
        partyName: foundParty.name?.replace(/^[^-]+-\s*/, '') || '',
        partyId: foundParty.id as number
      };
      setHarCutThirdPartyValidation(validationResult);
      setValidatedHarCutThirdPartyCode(trimmedCode);
      if (harCutThirdPartyCodeInputRef.current) {
        harCutThirdPartyCodeInputRef.current.value = foundParty.name || foundParty.code || '';
      }
      setHarCutThirdPartyCode(trimmedCode);
      setIsFieldValid(prev => ({
        ...prev,
        harCutThirdPartyCode: { isValid: true, message: '' }
      }));
      return validationResult;
    }

    try {
      const partyData = await apiService.get<{ id: number; partyCode?: string; partyName?: string }>(apiRoutes.parties.getByCode(trimmedCode));
      if (partyData && partyData.id && partyData.id !== 1) {
        const validationResult = {
          isValid: true,
          error: '',
          partyName: partyData.partyName || '',
          partyId: partyData.id
        };
        setHarCutThirdPartyValidation(validationResult);
        setValidatedHarCutThirdPartyCode(trimmedCode);
        if (harCutThirdPartyCodeInputRef.current) {
          harCutThirdPartyCodeInputRef.current.value = `${partyData.partyCode} - ${partyData.partyName}`;
        }
        setHarCutThirdPartyCode(trimmedCode);
        setIsFieldValid(prev => ({
          ...prev,
          harCutThirdPartyCode: { isValid: true, message: '' }
        }));
        return validationResult;
      }
      const validationResult = {
        isValid: false,
        error: 'Invalid party code',
        partyName: '',
        partyId: null
      };
      setHarCutThirdPartyValidation(validationResult);
      setValidatedHarCutThirdPartyCode('');
      setIsFieldValid(prev => ({
        ...prev,
        harCutThirdPartyCode: { isValid: false, message: 'Invalid party code' }
      }));
      return validationResult;
    } catch {
      const validationResult = {
        isValid: false,
        error: 'Invalid party code',
        partyName: '',
        partyId: null
      };
      setHarCutThirdPartyValidation(validationResult);
      setValidatedHarCutThirdPartyCode('');
      setIsFieldValid(prev => ({
        ...prev,
        harCutThirdPartyCode: { isValid: false, message: 'Invalid party code' }
      }));
      return validationResult;
    }
  };

  const hydrateHarCutThirdPartyFields = async (
    data: PartyFormData & { harCutThirdPartyId?: unknown; HarCutThirdPartyId?: unknown },
    codes: AutoCompleteOption[],
  ) => {
    const raw = data.harCutThirdPartyId ?? data.HarCutThirdPartyId;
    if (raw == null || raw === '' || Number(raw) === 0) {
      setHarCutThirdPartyValidation({ isValid: true, error: '', partyName: '', partyId: null });
      setValidatedHarCutThirdPartyCode('');
      setHarCutThirdPartyCode('');
      if (harCutThirdPartyCodeInputRef.current) {
        harCutThirdPartyCodeInputRef.current.value = '';
      }
      setIsFieldValid(prev => ({ ...prev, harCutThirdPartyCode: { isValid: true, message: '' } }));
      return;
    }
    const hid = Number(raw);
    const matching = codes.find(pc => Number(pc.id) === hid);
    if (matching) {
      setHarCutThirdPartyValidation({
        isValid: true,
        error: '',
        partyName: matching.name?.replace(/^[^-]+-\s*/, '') || '',
        partyId: matching.id as number
      });
      setValidatedHarCutThirdPartyCode((matching.code || '').toUpperCase());
      setHarCutThirdPartyCode((matching.code || '').toUpperCase());
      if (harCutThirdPartyCodeInputRef.current) {
        harCutThirdPartyCodeInputRef.current.value = matching.name || matching.code || '';
      }
      setIsFieldValid(prev => ({ ...prev, harCutThirdPartyCode: { isValid: true, message: '' } }));
      return;
    }
    try {
      const tp = await apiService.get<{ id: number; partyCode?: string; partyName?: string }>(apiRoutes.parties.getById(hid));
      if (tp && tp.id && tp.id !== 1) {
        const codeStr = (tp.partyCode || '').toUpperCase();
        setHarCutThirdPartyValidation({
          isValid: true,
          error: '',
          partyName: tp.partyName || '',
          partyId: tp.id
        });
        setValidatedHarCutThirdPartyCode(codeStr);
        setHarCutThirdPartyCode(codeStr);
        if (harCutThirdPartyCodeInputRef.current) {
          harCutThirdPartyCodeInputRef.current.value =
            tp.partyCode && tp.partyName ? `${tp.partyCode} - ${tp.partyName}` : tp.partyCode || codeStr;
        }
        setIsFieldValid(prev => ({ ...prev, harCutThirdPartyCode: { isValid: true, message: '' } }));
      }
    } catch {
      /* optional field — user can re-enter code */
    }
  };

  // Handle third party code input change
  const handleThirdPartyCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    
    // Extract code part if user pasted "CODE - NAME" format, otherwise use the typed value
    const codeMatch = value.match(/^([A-Z0-9]+)(\s*-\s*.*)?$/i);
    const codeOnly = codeMatch ? codeMatch[1].toUpperCase() : value.toUpperCase();
    
    // Check if user is typing a new value (different from validated code)
    const isNewValue = codeOnly !== validatedThirdPartyCode;
    
    if (isNewValue) {
      // User is typing a new value, clear validation and show only the code
      setThirdPartyCode(codeOnly);
      setValidatedThirdPartyCode('');
      setThirdPartyValidation({
        isValid: false,
        error: '',
        partyName: '',
        partyId: null
      });
      // Update input to show only the code (remove "CODE - NAME" format if present)
      if (thirdPartyCodeInputRef.current && value !== codeOnly) {
        thirdPartyCodeInputRef.current.value = codeOnly;
      }
      setIsFieldValid(prev => ({
        ...prev,
        thirdPartyCode: { isValid: true, message: '' }
      }));
    } else {
      // User hasn't changed the validated code, keep the display format
      setThirdPartyCode(codeOnly);
    }
  };

  // Handle third party code blur
  const handleThirdPartyCodeBlur = async () => {
    const value = thirdPartyCodeInputRef.current?.value || thirdPartyCode;
    // Extract code part if value contains "CODE - NAME" format
    const codeMatch = value.match(/^([A-Z0-9]+)(\s*-\s*.*)?$/i);
    const codeToValidate = codeMatch ? codeMatch[1].toUpperCase().trim() : value.trim().toUpperCase();
    
    if (codeToValidate) {
      // Only validate if it's a new value (different from validated code)
      if (codeToValidate !== validatedThirdPartyCode) {
        await validateAndFetchThirdParty(codeToValidate);
      }
    } else {
      // Empty value - clear validation (optional field)
      setThirdPartyValidation({
        isValid: true,
        error: '',
        partyName: '',
        partyId: null
      });
      setValidatedThirdPartyCode('');
      setThirdPartyCode('');
      if (thirdPartyCodeInputRef.current) {
        thirdPartyCodeInputRef.current.value = '';
      }
      setIsFieldValid(prev => ({
        ...prev,
        thirdPartyCode: { isValid: true, message: '' }
      }));
    }
  };

  const handleHarCutThirdPartyCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    const codeMatch = value.match(/^([A-Z0-9]+)(\s*-\s*.*)?$/i);
    const codeOnly = codeMatch ? codeMatch[1].toUpperCase() : value.toUpperCase();
    const isNewValue = codeOnly !== validatedHarCutThirdPartyCode;

    if (isNewValue) {
      setHarCutThirdPartyCode(codeOnly);
      setValidatedHarCutThirdPartyCode('');
      setHarCutThirdPartyValidation({
        isValid: false,
        error: '',
        partyName: '',
        partyId: null
      });
      if (harCutThirdPartyCodeInputRef.current && value !== codeOnly) {
        harCutThirdPartyCodeInputRef.current.value = codeOnly;
      }
      setIsFieldValid(prev => ({
        ...prev,
        harCutThirdPartyCode: { isValid: true, message: '' }
      }));
      setFormData(prev => ({ ...prev, thirdPartyHarCut: '0' }));
    } else {
      setHarCutThirdPartyCode(codeOnly);
    }
  };

  const handleHarCutThirdPartyCodeBlur = async () => {
    const value = harCutThirdPartyCodeInputRef.current?.value || harCutThirdPartyCode;
    const codeMatch = value.match(/^([A-Z0-9]+)(\s*-\s*.*)?$/i);
    const codeToValidate = codeMatch ? codeMatch[1].toUpperCase().trim() : value.trim().toUpperCase();

    if (codeToValidate) {
      if (codeToValidate !== validatedHarCutThirdPartyCode) {
        await validateAndFetchHarCutThirdParty(codeToValidate);
      }
    } else {
      setHarCutThirdPartyValidation({
        isValid: true,
        error: '',
        partyName: '',
        partyId: null
      });
      setValidatedHarCutThirdPartyCode('');
      setHarCutThirdPartyCode('');
      if (harCutThirdPartyCodeInputRef.current) {
        harCutThirdPartyCodeInputRef.current.value = '';
      }
      setFormData(prev => ({ ...prev, thirdPartyHarCut: '0' }));
      setIsFieldValid(prev => ({
        ...prev,
        harCutThirdPartyCode: { isValid: true, message: '' }
      }));
    }
  };

  const handleHarCutThirdPartyCodeEnter = async (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!e.currentTarget) return;
    const currentTarget = e.currentTarget;
    const value = harCutThirdPartyCodeInputRef.current?.value || harCutThirdPartyCode;
    const codeMatch = value.match(/^([A-Z0-9]+)(\s*-\s*.*)?$/i);
    const codeToValidate = codeMatch ? codeMatch[1].toUpperCase().trim() : value.trim().toUpperCase();

    if (codeToValidate && codeToValidate !== validatedHarCutThirdPartyCode) {
      try {
        await validateAndFetchHarCutThirdParty(codeToValidate);
      } catch {
        /* state updated in validate */
      }
    } else if (!codeToValidate) {
      await handleHarCutThirdPartyCodeBlur();
    }

    setTimeout(() => {
      if (!currentTarget?.form) return;
      focusNextFormField(currentTarget, currentTarget.form);
    }, 0);
  };

  // Handle third party code Enter key (similar to LagayaKhadha and Havala)
  const handleThirdPartyCodeEnter = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!e.currentTarget) return;
    const currentTarget = e.currentTarget;
    const value = thirdPartyCodeInputRef.current?.value || thirdPartyCode;
    const codeMatch = value.match(/^([A-Z0-9]+)(\s*-\s*.*)?$/i);
    const codeToValidate = codeMatch ? codeMatch[1].toUpperCase().trim() : value.trim().toUpperCase();

    if (codeToValidate && codeToValidate !== validatedThirdPartyCode) {
      validateAndFetchThirdParty(codeToValidate).catch(() => {});
    }

    setTimeout(() => {
      if (!currentTarget?.form) return;
      focusNextFormField(currentTarget, currentTarget.form);
    }, 50);
  };

  // Focus on first field when drawer opens, reset form when drawer closes
  useEffect(() => {
    if (isOpen) {
      // Delay focus to ensure drawer animation completes (300ms animation + small buffer)
      const focusTimer = setTimeout(() => {
        const partyCodeInput = document.getElementById('partyCode') as HTMLInputElement;
        partyCodeInput?.focus();
      }, 350);
      
      return () => clearTimeout(focusTimer);
    } else {
      // Reset commission input values when drawer closes
      setCommissionInputValues({});
      // Reset form when drawer closes - always reset regardless of edit/create mode
        setFormData(initialFormData);
        setDirtyFields({
          partyCode: false,
          partyName: false,
          city: false,
          remarks: false,
          phone: false,
        openingBalance: false,
        vatav: false,
        harCut: false,
        partners: [{
          partnerCode: false,
          partnerCommission: false,
        }],
        partnerCommission: false,
        partnerBCode: false,
        partnerBCommission: false,
        thirdPartyCode: false,
        sessionPartyCommission: false,
        sessionThirdPartyCommission: false,
        oneDayPartyCommission: false,
        oneDayThirdPartyCommission: false,
        testPartyCommission: false,
        testThirdPartyCommission: false,
        harCutThirdPartyCode: false,
        thirdPartyHarCut: false,
      });
        setIsFieldValid({
          partyCode: { isValid: true, message: "" },
          partyName: { isValid: true, message: "" },
          city: { isValid: true, message: "" },
          remarks: { isValid: true, message: "" },
          phone: { isValid: true, message: "" },
          openingBalance: { isValid: true, message: "" },
          vatav: { isValid: true, message: "" },
          harCut: { isValid: true, message: "" },
          partners: [{
            partnerCode: { isValid: true, message: "" },
            partnerCommission: { isValid: true, message: "" },
          }],
          partnerCommission: { isValid: true, message: "" },
          partnerBCode: { isValid: true, message: "" },
          partnerBCommission: { isValid: true, message: "" },
          thirdPartyCode: { isValid: true, message: "" },
          sessionPartyCommission: { isValid: true, message: "" },
          sessionThirdPartyCommission: { isValid: true, message: "" },
          oneDayPartyCommission: { isValid: true, message: "" },
          oneDayThirdPartyCommission: { isValid: true, message: "" },
          testPartyCommission: { isValid: true, message: "" },
          testThirdPartyCommission: { isValid: true, message: "" },
          harCutThirdPartyCode: { isValid: true, message: "" },
          thirdPartyHarCut: { isValid: true, message: "" },
        });
      setSelectedPartyCode({ id: 0, name: '', code: '' });
      setSelectedPartnerCode([{ id: 0, name: '', code: '' }]);
      // Reset third party code state
      setThirdPartyCode('');
      setValidatedThirdPartyCode('');
      setThirdPartyValidation({
        isValid: false,
        error: '',
        partyName: '',
        partyId: null
      });
      if (thirdPartyCodeInputRef.current) {
        thirdPartyCodeInputRef.current.value = '';
      }
      setHarCutThirdPartyCode('');
      setValidatedHarCutThirdPartyCode('');
      setHarCutThirdPartyValidation({
        isValid: false,
        error: '',
        partyName: '',
        partyId: null
      });
      if (harCutThirdPartyCodeInputRef.current) {
        harCutThirdPartyCodeInputRef.current.value = '';
      }
      // Reset partner code state
      setPartnerCodesState(['']);
      setValidatedPartnerCodes(['']);
      setPartnerValidations([{
        isValid: false,
        error: '',
        partyName: '',
        partyId: null
      }]);
      partnerCodeInputRefs.current.forEach(ref => {
        if (ref) {
          ref.value = '';
        }
      });
      partnerCodeInputRefs.current = [];
      }
  }, [isOpen]);

  // Set partner input values after refs are available (for edit mode)
  React.useEffect(() => {
    if (id && formData.partners.length > 0 && partnerValidations.length === formData.partners.length) {
      // Use setTimeout to ensure refs are available after render
      setTimeout(() => {
        formData.partners.forEach((partner, index) => {
          const partnerCode = partner.partnerCode;
          if (partnerCode && partnerValidations[index]?.isValid && partnerValidations[index]?.partyId) {
            // Find the matching party code from partyCodes state
            const matchingParty = partyCodes.find(pc => 
              Number(pc.id) === partnerValidations[index]?.partyId ||
              (pc.code || '').toUpperCase() === partnerCode.toUpperCase()
            );
            if (matchingParty && partnerCodeInputRefs.current[index]) {
              partnerCodeInputRefs.current[index]!.value = matchingParty.name || matchingParty.code || '';
            }
          }
        });
      }, 100);
    }
  }, [id, formData.partners, partnerValidations, partyCodes]);

  // Function to fetch party codes for autocomplete (can be called after Save & Continue)
  const fetchPartyCodes = async () => {
    try {
      // Use lite endpoint to get both code and name, exactly like GameBoard/LagayaKhadha
      const response = await apiService.post(apiRoutes.parties.lite, {
        page: 1,
        pageSize: 10000, // Get all parties for autocomplete
        orderBy: 'partyCode',
        orderDirection: 'asc'
      });
      
      // Handle response structure: { data: Party[], total?: number }
      const parties = response.data || [];
      if (parties.length > 0) {
      }
      
      if (Array.isArray(parties) && parties.length > 0) {
        // Format exactly like LagayaKhadha: "CODE - NAME"
        // Filter out parties with party code "CAS" (case-insensitive)
        const codes = parties
          .filter((party: any) => {
            const partyCode = (party.partyCode || party.code || '').toUpperCase();
            return partyCode !== 'CAS';
          })
          .map((party: any) => {
            // Handle both partyName and name fields
            const partyName = party.partyName || party.name || '';
            const partyCode = party.partyCode || party.code || '';
            
            return {
              id: party.id,
              name: partyName ? `${partyCode} - ${partyName}` : partyCode, // Full format in name field
              code: partyCode,
              displayName: partyName ? `${partyCode} - ${partyName}` : partyCode
            };
          });
        setPartyCodes(codes);
        
        return codes;
      }
      return [];
    } catch (error) {
      console.error('Error fetching party codes:', error);
      return [];
    }
  };

  // Fetch party codes before rendering Autocomplete
  React.useEffect(() => {
    if (isOpen) {
      fetchPartyCodes().then((codes): Promise<void> => {
        // If editing, fetch the party details after codes are loaded
        if (id && codes.length > 0) {
          return apiService.get<PartyFormData>(`/party/${id}`)
            .then(data => {
                  if (data) {
                    // Normalize partners to ensure partnerCode is always defined
                    // Map partnerPartyCode from API to partnerCode for form
                    // If no partners exist, initialize with at least one empty partner
                    const partnersArray = data.partners && data.partners.length > 0 
                      ? data.partners.map((p: any) => ({
                          partnerCode: p.partnerPartyCode || p.partnerCode || '',
                          partnerCommission: p.partnerCommission || 0
                        }))
                      : [{
                          partnerCode: '',
                          partnerCommission: 0
                        }];
                    
                    // Set Third Party Code based on thirdPartyId from API response (before normalizing data)
                    let thirdPartyCodeValue = '';
                    
                    if (data.thirdPartyId != null && data.thirdPartyId !== 0) {
                      // Convert to number for comparison (handle both string and number IDs)
                      const thirdPartyIdNum = Number(data.thirdPartyId);
                      const matchingThirdParty = codes.find(pc => Number(pc.id) === thirdPartyIdNum);
                      if (matchingThirdParty) {
                        thirdPartyCodeValue = matchingThirdParty.code || '';
                        // Set validation state and input value
                        setThirdPartyValidation({
                          isValid: true,
                          error: '',
                          partyName: matchingThirdParty.name?.replace(/^[^-]+-\s*/, '') || '',
                          partyId: matchingThirdParty.id as number
                        });
                        setValidatedThirdPartyCode(thirdPartyCodeValue.toUpperCase());
                        setThirdPartyCode(thirdPartyCodeValue.toUpperCase());
                        if (thirdPartyCodeInputRef.current) {
                          thirdPartyCodeInputRef.current.value = matchingThirdParty.name || matchingThirdParty.code || '';
                        }
                      } else {
                        // Fallback: try to find by thirdPartyCode if available (case-insensitive)
                        if (data.thirdPartyCode && data.thirdPartyCode.trim() !== '') {
                          const thirdPartyCodeUpper = data.thirdPartyCode.trim().toUpperCase();
                          const matchingByCode = codes.find(pc => (pc.code || '').toUpperCase() === thirdPartyCodeUpper);
                          if (matchingByCode) {
                            thirdPartyCodeValue = matchingByCode.code || '';
                            setThirdPartyValidation({
                              isValid: true,
                              error: '',
                              partyName: matchingByCode.name?.replace(/^[^-]+-\s*/, '') || '',
                              partyId: matchingByCode.id as number
                            });
                            setValidatedThirdPartyCode(thirdPartyCodeValue.toUpperCase());
                            setThirdPartyCode(thirdPartyCodeValue.toUpperCase());
                            if (thirdPartyCodeInputRef.current) {
                              thirdPartyCodeInputRef.current.value = matchingByCode.name || matchingByCode.code || '';
                            }
                          } else {
                            // Use the code from API response - validate and fetch
                            thirdPartyCodeValue = data.thirdPartyCode;
                            validateAndFetchThirdParty(data.thirdPartyCode).then(() => {
                              // Validation will set the state
                            });
                          }
                        } else {
                          // Clear third party code
                          setThirdPartyValidation({
                            isValid: true,
                            error: '',
                            partyName: '',
                            partyId: null
                          });
                          setValidatedThirdPartyCode('');
                          setThirdPartyCode('');
                          if (thirdPartyCodeInputRef.current) {
                            thirdPartyCodeInputRef.current.value = '';
                          }
                        }
                      }
                    } else {
                      // Clear third party code
                      setThirdPartyValidation({
                        isValid: true,
                        error: '',
                        partyName: '',
                        partyId: null
                      });
                      setValidatedThirdPartyCode('');
                      setThirdPartyCode('');
                      if (thirdPartyCodeInputRef.current) {
                        thirdPartyCodeInputRef.current.value = '';
                      }
                    }
                    
                    // Normalize numeric fields to ensure they're valid strings
                    const normalizedData = {
                      ...data,
                      partners: partnersArray,
                      vatav: data.vatav != null && data.vatav !== '' ? String(data.vatav) : '0',
                      harCut: normalizeHarCutFromApi(data),
                      thirdPartyHarCut: normalizeThirdPartyHarCutFromApi(data),
                      openingBalance: data.openingBalance != null && data.openingBalance !== '' ? String(data.openingBalance) : '0',
                      partnerCommission: data.partnerCommission != null && data.partnerCommission !== '' ? String(data.partnerCommission) : '0',
                      partnerBCommission: data.partnerBCommission != null && data.partnerBCommission !== '' ? String(data.partnerBCommission) : '0',
                      sessionPartyCommission: data.sessionPartyCommission != null && data.sessionPartyCommission !== '' ? String(data.sessionPartyCommission) : '0',
                      sessionThirdPartyCommission: data.sessionThirdPartyCommission != null && data.sessionThirdPartyCommission !== '' ? String(data.sessionThirdPartyCommission) : '0',
                      oneDayPartyCommission: data.oneDayPartyCommission != null && data.oneDayPartyCommission !== '' ? String(data.oneDayPartyCommission) : '0',
                      oneDayThirdPartyCommission: data.oneDayThirdPartyCommission != null && data.oneDayThirdPartyCommission !== '' ? String(data.oneDayThirdPartyCommission) : '0',
                      testPartyCommission: data.testPartyCommission != null && data.testPartyCommission !== '' ? String(data.testPartyCommission) : '0',
                      testThirdPartyCommission: data.testThirdPartyCommission != null && data.testThirdPartyCommission !== '' ? String(data.testThirdPartyCommission) : '0',
                      thirdPartyCode: thirdPartyCodeValue,
                      hideInGeneral: normalizeHideInGeneralFromApi(data),
                    };
                    void hydrateHarCutThirdPartyFields(normalizedData, codes);
                    setFormData(normalizedData);
                    
                    // Initialize dirtyFields and isFieldValid for partners and reset validation for numeric fields
                    setDirtyFields((prev) => ({
                      ...prev,
                      partners: partnersArray.map(() => ({
                        partnerCode: false,
                        partnerCommission: false
                      })),
                      vatav: false,
                      harCut: false,
                      openingBalance: false
                    }));
                    setIsFieldValid((prev) => ({
                      ...prev,
                      partners: partnersArray.map(() => ({
                        partnerCode: { isValid: true, message: "" },
                        partnerCommission: { isValid: true, message: "" }
                      })),
                      vatav: { isValid: true, message: "" },
                      harCut: { isValid: true, message: "" },
                      openingBalance: { isValid: true, message: "" }
                    }));
                    
                    // Set partner code validation state - map partnerPartyCode from API response
                    // Ensure the array length matches partnersArray
                    const partnerCodesArray: string[] = [];
                    const validatedPartnerCodesArray: string[] = [];
                    const partnerValidationsArray: Array<{ isValid: boolean; error: string; partyName: string; partyId: number | null }> = [];
                    
                    partnersArray.forEach((partner: any, index: number) => {
                      if (data.partners && data.partners[index]) {
                        const p = data.partners[index];
                        const partnerCode = (p.partnerPartyCode || p.partnerCode || '').trim();
                        
                        if (p.partnerId && partnerCode) {
                          // Try to find by partnerId first (convert to number for comparison), then by code (case-insensitive)
                          let matchingPartner = null;
                          if (p.partnerId) {
                            matchingPartner = codes.find(pc => Number(pc.id) === Number(p.partnerId));
                          }
                          if (!matchingPartner && partnerCode) {
                            const partnerCodeUpper = partnerCode.toUpperCase();
                            matchingPartner = codes.find(pc => (pc.code || '').toUpperCase() === partnerCodeUpper);
                          }
                          
                          if (matchingPartner) {
                            const partnerCodeUpper = partnerCode.toUpperCase();
                            partnerCodesArray.push(partnerCodeUpper);
                            validatedPartnerCodesArray.push(partnerCodeUpper);
                            partnerValidationsArray.push({
                              isValid: true,
                              error: '',
                              partyName: matchingPartner.name?.replace(/^[^-]+-\s*/, '') || '',
                              partyId: matchingPartner.id as number
                            });
                            // Set input value
                            if (partnerCodeInputRefs.current[index]) {
                              partnerCodeInputRefs.current[index]!.value = matchingPartner.name || matchingPartner.code || '';
                            }
                          } else {
                            // Try to validate and fetch
                            partnerCodesArray.push(partnerCode.toUpperCase());
                            validatedPartnerCodesArray.push('');
                            partnerValidationsArray.push({
                              isValid: false,
                              error: '',
                              partyName: '',
                              partyId: null
                            });
                            // Validate and fetch
                            validateAndFetchPartnerParty(partnerCode, index).then(() => {
                              // Validation will set the state
                            });
                          }
                        } else {
                          // Empty partner slot
                          partnerCodesArray.push('');
                          validatedPartnerCodesArray.push('');
                          partnerValidationsArray.push({
                            isValid: true,
                            error: '',
                            partyName: '',
                            partyId: null
                          });
                          if (partnerCodeInputRefs.current[index]) {
                            partnerCodeInputRefs.current[index]!.value = '';
                          }
                        }
                      } else {
                        // Empty partner slot
                        partnerCodesArray.push('');
                        validatedPartnerCodesArray.push('');
                        partnerValidationsArray.push({
                          isValid: true,
                          error: '',
                          partyName: '',
                          partyId: null
                        });
                      }
                    });
                    
                    // Initialize partner code input refs array with correct length
                    partnerCodeInputRefs.current = new Array(partnersArray.length).fill(null);
                    
                    // Initialize commission input values for all partners
                    const initialCommissionValues: Record<string, string> = {};
                    partnersArray.forEach((partner: any, index: number) => {
                      const commissionKey = `partnerCommission${index + 1}`;
                      initialCommissionValues[commissionKey] = partner.partnerCommission?.toString() || '0';
                    });
                    setCommissionInputValues(initialCommissionValues);
                    
                    setPartnerCodesState(partnerCodesArray);
                    setValidatedPartnerCodes(validatedPartnerCodesArray);
                    setPartnerValidations(partnerValidationsArray);
                  }
                })
                .catch(() => {
                  window.dispatchEvent(new CustomEvent('show-toast', { detail: { message: 'Error fetching party details', type: 'error' } }));
                });
        }
        // Return a resolved promise to maintain the chain
        return Promise.resolve();
      })
      .catch((error: any) => {
          console.error('Error fetching parties from lite endpoint:', error);
          // Fallback: Try regular Party endpoint
          apiService.post(apiRoutes.parties.list, {
            page: 1,
            pageSize: 1000,
            orderBy: 'partyCode',
            orderDirection: 'asc'
          })
            .then(response => {
              const parties = response.data || response;
              const partiesArray = Array.isArray(parties) ? parties : [];
              
              if (partiesArray.length > 0) {
                // Filter out parties with party code "CAS" (case-insensitive)
                const codes = partiesArray
                  .filter((party: any) => {
                    const partyCode = (party.partyCode || party.code || '').toUpperCase();
                    return partyCode !== 'CAS';
                  })
                  .map((party: any) => {
                    const partyName = party.partyName || party.name || '';
                    const partyCode = party.partyCode || party.code || '';
                    
                    return {
                      id: party.id,
                      name: partyName ? `${partyCode} - ${partyName}` : partyCode,
                      code: partyCode,
                      displayName: partyName ? `${partyCode} - ${partyName}` : partyCode
                    };
                  });
                setPartyCodes(codes);
                
                // Handle editing case
                if (id) {
                  apiService.get<PartyFormData>(`/party/${id}`)
                    .then(data => {
                      if (data) {
                    // Normalize partners to ensure partnerCode is always defined
                    // Map partnerPartyCode from API to partnerCode for form
                    // If no partners exist, initialize with at least one empty partner
                    const partnersArray = data.partners && data.partners.length > 0 
                      ? data.partners.map((p: any) => ({
                          partnerCode: p.partnerPartyCode || p.partnerCode || '',
                          partnerCommission: p.partnerCommission || 0
                        }))
                      : [{
                          partnerCode: '',
                          partnerCommission: 0
                        }];
                    
                    // Set Third Party Code based on thirdPartyId from API response (before normalizing data)
                    let thirdPartyCodeValue = '';
                    
                    if (data.thirdPartyId != null && data.thirdPartyId !== 0) {
                      // Convert to number for comparison (handle both string and number IDs)
                      const thirdPartyIdNum = Number(data.thirdPartyId);
                      const matchingThirdParty = codes.find(pc => Number(pc.id) === thirdPartyIdNum);
                      if (matchingThirdParty) {
                        thirdPartyCodeValue = matchingThirdParty.code || '';
                        // Set validation state and input value
                        setThirdPartyValidation({
                          isValid: true,
                          error: '',
                          partyName: matchingThirdParty.name?.replace(/^[^-]+-\s*/, '') || '',
                          partyId: matchingThirdParty.id as number
                        });
                        setValidatedThirdPartyCode(thirdPartyCodeValue.toUpperCase());
                        setThirdPartyCode(thirdPartyCodeValue.toUpperCase());
                        if (thirdPartyCodeInputRef.current) {
                          thirdPartyCodeInputRef.current.value = matchingThirdParty.name || matchingThirdParty.code || '';
                        }
                      } else {
                        // Fallback: try to find by thirdPartyCode if available (case-insensitive)
                        if (data.thirdPartyCode && data.thirdPartyCode.trim() !== '') {
                          const thirdPartyCodeUpper = data.thirdPartyCode.trim().toUpperCase();
                          const matchingByCode = codes.find(pc => (pc.code || '').toUpperCase() === thirdPartyCodeUpper);
                          if (matchingByCode) {
                            thirdPartyCodeValue = matchingByCode.code || '';
                            setThirdPartyValidation({
                              isValid: true,
                              error: '',
                              partyName: matchingByCode.name?.replace(/^[^-]+-\s*/, '') || '',
                              partyId: matchingByCode.id as number
                            });
                            setValidatedThirdPartyCode(thirdPartyCodeValue.toUpperCase());
                            setThirdPartyCode(thirdPartyCodeValue.toUpperCase());
                            if (thirdPartyCodeInputRef.current) {
                              thirdPartyCodeInputRef.current.value = matchingByCode.name || matchingByCode.code || '';
                            }
                          } else {
                            // Use the code from API response - validate and fetch
                            thirdPartyCodeValue = data.thirdPartyCode;
                            validateAndFetchThirdParty(data.thirdPartyCode).then(() => {
                              // Validation will set the state
                            });
                          }
                        } else {
                          // Clear third party code
                          setThirdPartyValidation({
                            isValid: true,
                            error: '',
                            partyName: '',
                            partyId: null
                          });
                          setValidatedThirdPartyCode('');
                          setThirdPartyCode('');
                          if (thirdPartyCodeInputRef.current) {
                            thirdPartyCodeInputRef.current.value = '';
                          }
                        }
                      }
                    } else {
                      // Clear third party code
                      setThirdPartyValidation({
                        isValid: true,
                        error: '',
                        partyName: '',
                        partyId: null
                      });
                      setValidatedThirdPartyCode('');
                      setThirdPartyCode('');
                      if (thirdPartyCodeInputRef.current) {
                        thirdPartyCodeInputRef.current.value = '';
                      }
                    }
                    
                    // Normalize numeric fields to ensure they're valid strings
                    const normalizedData = {
                      ...data,
                      partners: partnersArray,
                      vatav: data.vatav != null && data.vatav !== '' ? String(data.vatav) : '0',
                      harCut: normalizeHarCutFromApi(data),
                      thirdPartyHarCut: normalizeThirdPartyHarCutFromApi(data),
                      openingBalance: data.openingBalance != null && data.openingBalance !== '' ? String(data.openingBalance) : '0',
                      partnerCommission: data.partnerCommission != null && data.partnerCommission !== '' ? String(data.partnerCommission) : '0',
                      partnerBCommission: data.partnerBCommission != null && data.partnerBCommission !== '' ? String(data.partnerBCommission) : '0',
                      sessionPartyCommission: data.sessionPartyCommission != null && data.sessionPartyCommission !== '' ? String(data.sessionPartyCommission) : '0',
                      sessionThirdPartyCommission: data.sessionThirdPartyCommission != null && data.sessionThirdPartyCommission !== '' ? String(data.sessionThirdPartyCommission) : '0',
                      oneDayPartyCommission: data.oneDayPartyCommission != null && data.oneDayPartyCommission !== '' ? String(data.oneDayPartyCommission) : '0',
                      oneDayThirdPartyCommission: data.oneDayThirdPartyCommission != null && data.oneDayThirdPartyCommission !== '' ? String(data.oneDayThirdPartyCommission) : '0',
                      testPartyCommission: data.testPartyCommission != null && data.testPartyCommission !== '' ? String(data.testPartyCommission) : '0',
                      testThirdPartyCommission: data.testThirdPartyCommission != null && data.testThirdPartyCommission !== '' ? String(data.testThirdPartyCommission) : '0',
                      thirdPartyCode: thirdPartyCodeValue,
                      hideInGeneral: normalizeHideInGeneralFromApi(data),
                    };
                    void hydrateHarCutThirdPartyFields(normalizedData, codes);
                    setFormData(normalizedData);
                    
                    // Initialize dirtyFields and isFieldValid for partners and reset validation for numeric fields
                    setDirtyFields((prev) => ({
                      ...prev,
                      partners: partnersArray.map(() => ({
                        partnerCode: false,
                        partnerCommission: false
                      })),
                      vatav: false,
                      harCut: false,
                      openingBalance: false
                    }));
                    setIsFieldValid((prev) => ({
                      ...prev,
                      partners: partnersArray.map(() => ({
                        partnerCode: { isValid: true, message: "" },
                        partnerCommission: { isValid: true, message: "" }
                      })),
                      vatav: { isValid: true, message: "" },
                      harCut: { isValid: true, message: "" },
                      openingBalance: { isValid: true, message: "" }
                    }));
                    
                    // Set partner code validation state - map partnerPartyCode from API response
                    // Ensure the array length matches partnersArray
                    const partnerCodesArray: string[] = [];
                    const validatedPartnerCodesArray: string[] = [];
                    const partnerValidationsArray: Array<{ isValid: boolean; error: string; partyName: string; partyId: number | null }> = [];
                    
                    partnersArray.forEach((partner: any, index: number) => {
                      if (data.partners && data.partners[index]) {
                        const p = data.partners[index];
                        const partnerCode = (p.partnerPartyCode || p.partnerCode || '').trim();
                        
                        if (p.partnerId && partnerCode) {
                          // Try to find by partnerId first (convert to number for comparison), then by code (case-insensitive)
                          let matchingPartner = null;
                          if (p.partnerId) {
                            matchingPartner = codes.find(pc => Number(pc.id) === Number(p.partnerId));
                          }
                          if (!matchingPartner && partnerCode) {
                            const partnerCodeUpper = partnerCode.toUpperCase();
                            matchingPartner = codes.find(pc => (pc.code || '').toUpperCase() === partnerCodeUpper);
                          }
                          
                          if (matchingPartner) {
                            const partnerCodeUpper = partnerCode.toUpperCase();
                            partnerCodesArray.push(partnerCodeUpper);
                            validatedPartnerCodesArray.push(partnerCodeUpper);
                            partnerValidationsArray.push({
                              isValid: true,
                              error: '',
                              partyName: matchingPartner.name?.replace(/^[^-]+-\s*/, '') || '',
                              partyId: matchingPartner.id as number
                            });
                            // Set input value
                            if (partnerCodeInputRefs.current[index]) {
                              partnerCodeInputRefs.current[index]!.value = matchingPartner.name || matchingPartner.code || '';
                            }
                          } else {
                            // Try to validate and fetch
                            partnerCodesArray.push(partnerCode.toUpperCase());
                            validatedPartnerCodesArray.push('');
                            partnerValidationsArray.push({
                              isValid: false,
                              error: '',
                              partyName: '',
                              partyId: null
                            });
                            // Validate and fetch
                            validateAndFetchPartnerParty(partnerCode, index).then(() => {
                              // Validation will set the state
                            });
                          }
                        } else {
                          // Empty partner slot
                          partnerCodesArray.push('');
                          validatedPartnerCodesArray.push('');
                          partnerValidationsArray.push({
                            isValid: true,
                            error: '',
                            partyName: '',
                            partyId: null
                          });
                          if (partnerCodeInputRefs.current[index]) {
                            partnerCodeInputRefs.current[index]!.value = '';
                          }
                        }
                      } else {
                        // Empty partner slot
                        partnerCodesArray.push('');
                        validatedPartnerCodesArray.push('');
                        partnerValidationsArray.push({
                          isValid: true,
                          error: '',
                          partyName: '',
                          partyId: null
                        });
                      }
                    });
                    
                    // Initialize partner code input refs array with correct length
                    partnerCodeInputRefs.current = new Array(partnersArray.length).fill(null);
                    
                    // Initialize commission input values for all partners
                    const initialCommissionValues: Record<string, string> = {};
                    partnersArray.forEach((partner: any, index: number) => {
                      const commissionKey = `partnerCommission${index + 1}`;
                      initialCommissionValues[commissionKey] = partner.partnerCommission?.toString() || '0';
                    });
                    setCommissionInputValues(initialCommissionValues);
                    
                    setPartnerCodesState(partnerCodesArray);
                    setValidatedPartnerCodes(validatedPartnerCodesArray);
                    setPartnerValidations(partnerValidationsArray);
                      }
                    })
                    .catch(() => {
                      window.dispatchEvent(new CustomEvent('show-toast', { detail: { message: 'Error fetching party details', type: 'error' } }));
                    });
                }
              }
            })
            .catch(() => {
              // Final fallback to getPartyCodes
              apiService.get<PartyCode[]>('/party/getPartyCodes') 
                .then(data => {
                  if (Array.isArray(data)) {
                    // Filter out parties with party code "CAS" (case-insensitive)
                    const codes = data
                      .filter((code: any) => {
                        const partyCode = (code.partyCode || code.code || '').toUpperCase();
                        return partyCode !== 'CAS';
                      })
                      .map((code: any) => {
                        const partyName = code.partyName || code.name || '';
                        const partyCode = code.partyCode || code.code || '';
                        
                        return {
                          id: code.id,
                          name: partyName ? `${partyCode} - ${partyName}` : partyCode,
                          code: partyCode,
                          displayName: partyName ? `${partyCode} - ${partyName}` : partyCode
                        };
                      });
                    setPartyCodes(codes);
                    
                    // If editing, fetch the party details after codes are loaded
                    if (id) {
                      apiService.get<PartyFormData>(`/party/${id}`)
                        .then(data => {
                          if (data) {
                    // Normalize partners to ensure partnerCode is always defined
                    // Map partnerPartyCode from API to partnerCode for form
                    // If no partners exist, initialize with at least one empty partner
                    const partnersArray = data.partners && data.partners.length > 0 
                      ? data.partners.map((p: any) => ({
                          partnerCode: p.partnerPartyCode || p.partnerCode || '',
                          partnerCommission: p.partnerCommission || 0
                        }))
                      : [{
                          partnerCode: '',
                          partnerCommission: 0
                        }];
                    
                    // Set Third Party Code based on thirdPartyId from API response (before normalizing data)
                    let thirdPartyCodeValue = '';
                    
                    if (data.thirdPartyId != null && data.thirdPartyId !== 0) {
                      // Convert to number for comparison (handle both string and number IDs)
                      const thirdPartyIdNum = Number(data.thirdPartyId);
                      const matchingThirdParty = codes.find(pc => Number(pc.id) === thirdPartyIdNum);
                      if (matchingThirdParty) {
                        thirdPartyCodeValue = matchingThirdParty.code || '';
                        // Set validation state and input value
                        setThirdPartyValidation({
                          isValid: true,
                          error: '',
                          partyName: matchingThirdParty.name?.replace(/^[^-]+-\s*/, '') || '',
                          partyId: matchingThirdParty.id as number
                        });
                        setValidatedThirdPartyCode(thirdPartyCodeValue.toUpperCase());
                        setThirdPartyCode(thirdPartyCodeValue.toUpperCase());
                        if (thirdPartyCodeInputRef.current) {
                          thirdPartyCodeInputRef.current.value = matchingThirdParty.name || matchingThirdParty.code || '';
                        }
                      } else {
                        // Fallback: try to find by thirdPartyCode if available (case-insensitive)
                        if (data.thirdPartyCode && data.thirdPartyCode.trim() !== '') {
                          const thirdPartyCodeUpper = data.thirdPartyCode.trim().toUpperCase();
                          const matchingByCode = codes.find(pc => (pc.code || '').toUpperCase() === thirdPartyCodeUpper);
                          if (matchingByCode) {
                            thirdPartyCodeValue = matchingByCode.code || '';
                            setThirdPartyValidation({
                              isValid: true,
                              error: '',
                              partyName: matchingByCode.name?.replace(/^[^-]+-\s*/, '') || '',
                              partyId: matchingByCode.id as number
                            });
                            setValidatedThirdPartyCode(thirdPartyCodeValue.toUpperCase());
                            setThirdPartyCode(thirdPartyCodeValue.toUpperCase());
                            if (thirdPartyCodeInputRef.current) {
                              thirdPartyCodeInputRef.current.value = matchingByCode.name || matchingByCode.code || '';
                            }
                          } else {
                            // Use the code from API response - validate and fetch
                            thirdPartyCodeValue = data.thirdPartyCode;
                            validateAndFetchThirdParty(data.thirdPartyCode).then(() => {
                              // Validation will set the state
                            });
                          }
                        } else {
                          // Clear third party code
                          setThirdPartyValidation({
                            isValid: true,
                            error: '',
                            partyName: '',
                            partyId: null
                          });
                          setValidatedThirdPartyCode('');
                          setThirdPartyCode('');
                          if (thirdPartyCodeInputRef.current) {
                            thirdPartyCodeInputRef.current.value = '';
                          }
                        }
                      }
                    } else {
                      // Clear third party code
                      setThirdPartyValidation({
                        isValid: true,
                        error: '',
                        partyName: '',
                        partyId: null
                      });
                      setValidatedThirdPartyCode('');
                      setThirdPartyCode('');
                      if (thirdPartyCodeInputRef.current) {
                        thirdPartyCodeInputRef.current.value = '';
                      }
                    }
                    
                    // Normalize numeric fields to ensure they're valid strings
                    const normalizedData = {
                      ...data,
                      partners: partnersArray,
                      vatav: data.vatav != null && data.vatav !== '' ? String(data.vatav) : '0',
                      harCut: normalizeHarCutFromApi(data),
                      thirdPartyHarCut: normalizeThirdPartyHarCutFromApi(data),
                      openingBalance: data.openingBalance != null && data.openingBalance !== '' ? String(data.openingBalance) : '0',
                      partnerCommission: data.partnerCommission != null && data.partnerCommission !== '' ? String(data.partnerCommission) : '0',
                      partnerBCommission: data.partnerBCommission != null && data.partnerBCommission !== '' ? String(data.partnerBCommission) : '0',
                      sessionPartyCommission: data.sessionPartyCommission != null && data.sessionPartyCommission !== '' ? String(data.sessionPartyCommission) : '0',
                      sessionThirdPartyCommission: data.sessionThirdPartyCommission != null && data.sessionThirdPartyCommission !== '' ? String(data.sessionThirdPartyCommission) : '0',
                      oneDayPartyCommission: data.oneDayPartyCommission != null && data.oneDayPartyCommission !== '' ? String(data.oneDayPartyCommission) : '0',
                      oneDayThirdPartyCommission: data.oneDayThirdPartyCommission != null && data.oneDayThirdPartyCommission !== '' ? String(data.oneDayThirdPartyCommission) : '0',
                      testPartyCommission: data.testPartyCommission != null && data.testPartyCommission !== '' ? String(data.testPartyCommission) : '0',
                      testThirdPartyCommission: data.testThirdPartyCommission != null && data.testThirdPartyCommission !== '' ? String(data.testThirdPartyCommission) : '0',
                      thirdPartyCode: thirdPartyCodeValue,
                      hideInGeneral: normalizeHideInGeneralFromApi(data),
                    };
                    void hydrateHarCutThirdPartyFields(normalizedData, codes);
                    setFormData(normalizedData);
                    
                    // Initialize dirtyFields and isFieldValid for partners and reset validation for numeric fields
                    setDirtyFields((prev) => ({
                      ...prev,
                      partners: partnersArray.map(() => ({
                        partnerCode: false,
                        partnerCommission: false
                      })),
                      vatav: false,
                      harCut: false,
                      openingBalance: false
                    }));
                    setIsFieldValid((prev) => ({
                      ...prev,
                      partners: partnersArray.map(() => ({
                        partnerCode: { isValid: true, message: "" },
                        partnerCommission: { isValid: true, message: "" }
                      })),
                      vatav: { isValid: true, message: "" },
                      harCut: { isValid: true, message: "" },
                      openingBalance: { isValid: true, message: "" }
                    }));
                    
                    // Set partner code validation state - map partnerPartyCode from API response
                    // Ensure the array length matches partnersArray
                    const partnerCodesArray: string[] = [];
                    const validatedPartnerCodesArray: string[] = [];
                    const partnerValidationsArray: Array<{ isValid: boolean; error: string; partyName: string; partyId: number | null }> = [];
                    
                    partnersArray.forEach((partner: any, index: number) => {
                      if (data.partners && data.partners[index]) {
                        const p = data.partners[index];
                        const partnerCode = (p.partnerPartyCode || p.partnerCode || '').trim();
                        
                        if (p.partnerId && partnerCode) {
                          // Try to find by partnerId first (convert to number for comparison), then by code (case-insensitive)
                          let matchingPartner = null;
                          if (p.partnerId) {
                            matchingPartner = codes.find(pc => Number(pc.id) === Number(p.partnerId));
                          }
                          if (!matchingPartner && partnerCode) {
                            const partnerCodeUpper = partnerCode.toUpperCase();
                            matchingPartner = codes.find(pc => (pc.code || '').toUpperCase() === partnerCodeUpper);
                          }
                          
                          if (matchingPartner) {
                            const partnerCodeUpper = partnerCode.toUpperCase();
                            partnerCodesArray.push(partnerCodeUpper);
                            validatedPartnerCodesArray.push(partnerCodeUpper);
                            partnerValidationsArray.push({
                              isValid: true,
                              error: '',
                              partyName: matchingPartner.name?.replace(/^[^-]+-\s*/, '') || '',
                              partyId: matchingPartner.id as number
                            });
                            // Set input value
                            if (partnerCodeInputRefs.current[index]) {
                              partnerCodeInputRefs.current[index]!.value = matchingPartner.name || matchingPartner.code || '';
                            }
                          } else {
                            // Try to validate and fetch
                            partnerCodesArray.push(partnerCode.toUpperCase());
                            validatedPartnerCodesArray.push('');
                            partnerValidationsArray.push({
                              isValid: false,
                              error: '',
                              partyName: '',
                              partyId: null
                            });
                            // Validate and fetch
                            validateAndFetchPartnerParty(partnerCode, index).then(() => {
                              // Validation will set the state
                            });
                          }
                        } else {
                          // Empty partner slot
                          partnerCodesArray.push('');
                          validatedPartnerCodesArray.push('');
                          partnerValidationsArray.push({
                            isValid: true,
                            error: '',
                            partyName: '',
                            partyId: null
                          });
                          if (partnerCodeInputRefs.current[index]) {
                            partnerCodeInputRefs.current[index]!.value = '';
                          }
                        }
                      } else {
                        // Empty partner slot
                        partnerCodesArray.push('');
                        validatedPartnerCodesArray.push('');
                        partnerValidationsArray.push({
                          isValid: true,
                          error: '',
                          partyName: '',
                          partyId: null
                        });
                      }
                    });
                    
                    // Initialize partner code input refs array with correct length
                    partnerCodeInputRefs.current = new Array(partnersArray.length).fill(null);
                    
                    // Initialize commission input values for all partners
                    const initialCommissionValues: Record<string, string> = {};
                    partnersArray.forEach((partner: any, index: number) => {
                      const commissionKey = `partnerCommission${index + 1}`;
                      initialCommissionValues[commissionKey] = partner.partnerCommission?.toString() || '0';
                    });
                    setCommissionInputValues(initialCommissionValues);
                    
                    setPartnerCodesState(partnerCodesArray);
                    setValidatedPartnerCodes(validatedPartnerCodesArray);
                    setPartnerValidations(partnerValidationsArray);
                          }
                        })
                        .catch(() => {
                          window.dispatchEvent(new CustomEvent('show-toast', { detail: { message: 'Error fetching party details', type: 'error' } }));
                        });
                    }
                  }
                })
                .catch(() => setPartyCodes([]));
            });
        });
    }
  }, [isOpen, id]);

  return (
    <SideDrawer isOpen={isOpen} onClose={() => onClose(false)} title={id ? "Edit Party" : "Create Party"} width="w-[min(700px,calc(100vw-1.5rem))]">
      <form onSubmit={handleSubmit} className="space-y-2 max-w-full min-w-0 overflow-x-hidden">
        {/* Grouped Fields: Party Code | Party Name — equal halves; min-w-0 prevents overlap */}
        <div className="flex gap-2 sm:gap-4 min-w-0 items-start">
          <div className="min-w-0 flex-1 basis-0">
            <InputField 
              label="Party Code" 
              labelClassName="text-gray-700"
              onKeyDown={handleKeyDown} 
              maxLength={5} 
              name="partyCode" 
              value={formData.partyCode} 
              onChange={handleChange} 
              onBlur={handleBlur} 
              className={`${!isFieldValid.partyCode.isValid && dirtyFields.partyCode ? "border-2 border-red-600" : ""}`} 
              error={isFieldValid.partyCode.message} 
            />
          </div>
          <div className="min-w-0 flex-1 basis-0">
            <InputField 
              displayLabel={false} 
              onKeyDown={handleKeyDown} 
              maxLength={50} 
              label="Party Name" 
              name="partyName" 
              value={formData.partyName} 
              onChange={handleChange} 
              className={!isFieldValid.partyName.isValid && dirtyFields.partyName ? "border-2 border-red-600" : ""} 
              error={isFieldValid.partyName.message} 
            />
          </div>
        </div>

        <InputField label="City" labelClassName="text-gray-700" name="city" onKeyDown={handleKeyDown} maxLength={30} value={formData.city} onChange={handleChange} className={!isFieldValid.city.isValid && dirtyFields.city ? "border-2 border-red-600" : ""} error={isFieldValid.city.message} />
        <InputField label="Remarks" labelClassName="text-gray-700" name="remarks" onKeyDown={handleKeyDown} maxLength={255} value={formData.remarks} onChange={handleChange} className={!isFieldValid.remarks.isValid && dirtyFields.remarks ? "border-2 border-red-600" : ""} error={isFieldValid.remarks.message} />
        <InputField label="Phone" labelClassName="text-gray-700" name="phone" onKeyDown={handleKeyDown} type="text" max={9999999999} maxLength={10} value={formData.phone} onChange={handleChange} className={!isFieldValid.phone.isValid && dirtyFields.phone ? "border-2 border-red-600" : ""} error={isFieldValid.phone.message} />
        <InputField label="Opening Balance" labelClassName="text-gray-700" onKeyDown={handleKeyDown} min={-99999999999} max={99999999999} maxLength={12} type='text' name="openingBalance" value={formData.openingBalance} onChange={handleChange} disabled={!!id} readOnly={!!id} className={`${!isFieldValid.openingBalance.isValid && dirtyFields.openingBalance ? "border-2 border-red-600" : ""} ${id ? "bg-gray-200 cursor-not-allowed" : ""}`} error={isFieldValid.openingBalance.message} />
        <InputField label="Vatav" labelClassName="text-gray-700" name="vatav" onKeyDown={handleKeyDown} min={0} max={99} maxLength={2} type='text' value={formData.vatav} onChange={handleChange} className={!isFieldValid.vatav.isValid && dirtyFields.vatav ? "border-2 border-red-600" : ""} error={isFieldValid.vatav.message} />
        <InputField label="HarCut (0–99999)" labelClassName="text-gray-700" name="harCut" onKeyDown={handleKeyDown} min={0} max={99999} maxLength={5} type="text" inputMode="numeric" value={formData.harCut} onChange={handleChange} onBlur={handleBlur} className={!isFieldValid.harCut.isValid && dirtyFields.harCut ? "border-2 border-red-600" : ""} error={isFieldValid.harCut.message} />

        <div className="flex flex-col gap-2 sm:flex-row sm:gap-4 sm:items-end min-w-0">
          <div className="flex-1 flex flex-col gap-[2px] mb-0 w-full min-w-0">
            <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-[3px] w-full min-w-0">
              <label htmlFor="harCutThirdPartyCode" className="w-[4.75rem] sm:w-28 md:w-36 lg:w-48 shrink-0 font-medium text-gray-700">
                HarCut Third Party Code
              </label>
              <div className="flex-1 min-w-0">
                <input
                  ref={harCutThirdPartyCodeInputRef}
                  id="harCutThirdPartyCode"
                  name="harCutThirdPartyCode"
                  type="text"
                  onChange={handleHarCutThirdPartyCodeChange}
                  onBlur={handleHarCutThirdPartyCodeBlur}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      e.stopPropagation();
                      void handleHarCutThirdPartyCodeEnter(e);
                    }
                  }}
                  maxLength={50}
                  placeholder="Party Code"
                  className={`w-full h-[30px] px-2 py-1 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                    harCutThirdPartyValidation.error ? 'border-red-600' :
                    harCutThirdPartyValidation.isValid && harCutThirdPartyValidation.partyId ? 'border-green-600' :
                    'border-gray-300'
                  } bg-white text-gray-900`}
                />
              </div>
            </div>
            {harCutThirdPartyValidation.error && (
              <div className="text-red-500 text-sm mt-0.5 ml-[4.75rem] sm:ml-28 md:ml-36 lg:ml-48">
                {harCutThirdPartyValidation.error}
              </div>
            )}
          </div>
          <div className="w-full sm:w-[140px] sm:flex-shrink-0 min-w-0">
            <InputField
              displayLabel={false}
              label="Third Party HarCut"
              placeholder=""
              aria-label="Third Party HarCut"
              name="thirdPartyHarCut"
              onKeyDown={handleKeyDown}
              min={0}
              max={99999}
              maxLength={5}
              type="text"
              inputMode="numeric"
              value={formData.thirdPartyHarCut}
              onChange={handleChange}
              onBlur={handleBlur}
              disabled={!harCutThirdPartyValidation.isValid || !harCutThirdPartyValidation.partyId}
              className={`${!isFieldValid.thirdPartyHarCut.isValid && dirtyFields.thirdPartyHarCut ? "border-2 border-red-600" : ""} ${(!harCutThirdPartyValidation.isValid || !harCutThirdPartyValidation.partyId) ? "bg-gray-200" : ""}`}
              error={isFieldValid.thirdPartyHarCut.message}
            />
          </div>
        </div>

        {formData.partners.map((partner, index) => (
          <div className="flex gap-2 items-end" key={index}>
            <div className="flex-1 flex gap-4">
              <div className="flex-[6]">
                <div className="flex flex-col gap-[2px] mb-0 w-full">
                  <div className="flex items-center gap-[3px] w-full">
                    <label htmlFor={`partnerCode${index + 1}`} className="w-48 shrink-0 font-medium text-gray-700">
                      Partner Code {index + 1}
                    </label>
                    <div className="flex-1">
                      <input
                        ref={(el) => {
                          partnerCodeInputRefs.current[index] = el;
                        }}
                        id={`partnerCode${index + 1}`}
                        name={`partnerCode${index + 1}`}
                        type="text"
                        onChange={(e) => handlePartnerCodeChange(e, index)}
                        onBlur={() => handlePartnerCodeBlur(index)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            e.stopPropagation();
                            handlePartnerCodeEnter(e, index);
                          }
                        }}
                        maxLength={50}
                        placeholder="Party Code"
                        className={`flex-1 h-[30px] px-2 py-1 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                          partnerValidations[index]?.error ? 'border-red-600' : 
                          partnerValidations[index]?.isValid ? 'border-green-600' : 
                          'border-gray-300'
                        } bg-white text-gray-900`}
                      />
                    </div>
                  </div>
                  {partnerValidations[index]?.error && (
                    <div className="text-red-500 text-sm mt-0.5 ml-[4.75rem] sm:ml-28 md:ml-36 lg:ml-48">
                      {partnerValidations[index].error}
                    </div>
                  )}
                </div>
              </div>
              <div className="w-[min(7.5rem,28vw)] sm:w-[80px] flex-shrink-0 min-w-[5.5rem] overflow-hidden">
                <InputField displayLabel={false} max={999} onKeyDown={handleKeyDown} min={-999} maxLength={8} type='text' label={`Partner Commission ${index + 1}`} name={`partnerCommission${index + 1}`} value={commissionInputValues[`partnerCommission${index + 1}`] !== undefined ? commissionInputValues[`partnerCommission${index + 1}`] : partner.partnerCommission.toString()} onChange={handleChange} className={!isFieldValid.partners[index].partnerCommission.isValid && dirtyFields.partners[index].partnerCommission ? "border-2 border-red-600" : ""} disabled={!partnerValidations[index]?.isValid || !partnerValidations[index]?.partyId} error={isFieldValid.partners[index].partnerCommission.message} />
              </div>
            </div>

            <div className="flex gap-1 items-center h-[30px]">
              {formData.partners.length > 1 ? <TrashIcon className="bg-gray-200 hover:bg-gray-300 p-1 rounded h-6 w-6 cursor-pointer transition-colors" onClick={() => handleDeletePartner(index)} /> : null}
              {formData.partners.length === index + 1 && formData.partners.length < MAX_PARTNERS_ALLOWED ? <PlusIcon className="bg-gray-200 hover:bg-gray-300 p-1 rounded h-6 w-6 cursor-pointer transition-colors" onClick={handleAddPartner} /> : null}
            </div>
          </div>
        ))}

        <div className="flex flex-col gap-[2px] mb-0 w-full min-w-0">
          <div className="flex items-center gap-[3px] w-full min-w-0">
            <label htmlFor="thirdPartyCode" className="w-[4.75rem] sm:w-28 md:w-36 lg:w-48 shrink-0 font-medium text-gray-700">
              Third Party Code
            </label>
            <div className="flex-1">
              <input
                ref={thirdPartyCodeInputRef}
                id="thirdPartyCode"
                name="thirdPartyCode"
                type="text"
                onChange={handleThirdPartyCodeChange}
                onBlur={handleThirdPartyCodeBlur}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    e.stopPropagation();
                    handleThirdPartyCodeEnter(e);
                  }
                }}
                maxLength={50}
                placeholder="Party Code"
                className={`flex-1 h-[30px] px-2 py-1 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                  thirdPartyValidation.error ? 'border-red-600' : 
                  thirdPartyValidation.isValid ? 'border-green-600' : 
                  'border-gray-300'
                } bg-white text-gray-900`}
              />
            </div>
          </div>
          {thirdPartyValidation.error && (
            <div className="text-red-500 text-sm mt-0.5 ml-[4.75rem] sm:ml-28 md:ml-36 lg:ml-48">
              {thirdPartyValidation.error}
            </div>
          )}
        </div>

        <div className="flex gap-2 sm:gap-4 min-w-0 mb-1 pt-1 border-t border-gray-200 text-gray-700 font-medium text-xs sm:text-sm">
          <div className="min-w-0 flex-1 basis-0 text-left truncate" title="Party Commission">Party Commission</div>
          <div className="min-w-0 flex-1 basis-0 text-left truncate" title="Third Party Commission">Third Party Commission</div>
        </div>
        <div className="flex gap-2 sm:gap-4 min-w-0 items-start">
          <div className="min-w-0 flex-1 basis-0">
            <InputField label="Session Commission" labelClassName="text-gray-700" onKeyDown={handleKeyDown} min={-999} max={999} maxLength={8} type='text' name="sessionPartyCommission" value={commissionInputValues['sessionPartyCommission'] !== undefined ? commissionInputValues['sessionPartyCommission'] : (formData.sessionPartyCommission || '0')} className={!isFieldValid.sessionPartyCommission.isValid && dirtyFields.sessionPartyCommission ? "border-2 border-red-600" : ""} onChange={handleChange} error={isFieldValid.sessionPartyCommission.message} />
          </div>
          <div className="min-w-0 flex-1 basis-0">
            <InputField displayLabel={false} onKeyDown={handleKeyDown} min={-999} max={999} maxLength={8} type='text' label="Session Third Party Commission" name="sessionThirdPartyCommission" value={commissionInputValues['sessionThirdPartyCommission'] !== undefined ? commissionInputValues['sessionThirdPartyCommission'] : (formData.sessionThirdPartyCommission || '0')} onChange={handleChange} className={`${!isFieldValid.sessionThirdPartyCommission.isValid && dirtyFields.sessionThirdPartyCommission ? "border-2 border-red-600" : ""} ${(!thirdPartyValidation.isValid || !thirdPartyValidation.partyId) ? "bg-gray-200" : ""}`} disabled={!thirdPartyValidation.isValid || !thirdPartyValidation.partyId} error={isFieldValid.sessionThirdPartyCommission.message} />
          </div>
        </div>

        <div className="flex gap-2 sm:gap-4 min-w-0 items-start">
          <div className="min-w-0 flex-1 basis-0">
            <InputField label="One Day Commission" labelClassName="text-gray-700" onKeyDown={handleKeyDown} min={-999} max={999} maxLength={8} type='text' name="oneDayPartyCommission" value={commissionInputValues['oneDayPartyCommission'] !== undefined ? commissionInputValues['oneDayPartyCommission'] : (formData.oneDayPartyCommission || '0')} className={!isFieldValid.oneDayPartyCommission.isValid && dirtyFields.oneDayPartyCommission ? "border-2 border-red-600" : ""} onChange={handleChange} error={isFieldValid.oneDayPartyCommission.message} />
          </div>
          <div className="min-w-0 flex-1 basis-0">
            <InputField displayLabel={false} onKeyDown={handleKeyDown} min={-999} max={999} maxLength={8} type='text' label="One Day Third Party Commission" name="oneDayThirdPartyCommission" value={commissionInputValues['oneDayThirdPartyCommission'] !== undefined ? commissionInputValues['oneDayThirdPartyCommission'] : (formData.oneDayThirdPartyCommission || '0')} onChange={handleChange} className={`${!isFieldValid.oneDayThirdPartyCommission.isValid && dirtyFields.oneDayThirdPartyCommission ? "border-2 border-red-600" : ""} ${(!thirdPartyValidation.isValid || !thirdPartyValidation.partyId) ? "bg-gray-200" : ""}`} disabled={!thirdPartyValidation.isValid || !thirdPartyValidation.partyId} error={isFieldValid.oneDayThirdPartyCommission.message} />
          </div>
        </div>

        <div className="flex gap-2 sm:gap-4 min-w-0 items-start">
          <div className="min-w-0 flex-1 basis-0">
            <InputField label="Test Commission" labelClassName="text-gray-700" onKeyDown={handleKeyDown} min={-999} max={999} maxLength={8} type='text' name="testPartyCommission" value={commissionInputValues['testPartyCommission'] !== undefined ? commissionInputValues['testPartyCommission'] : (formData.testPartyCommission || '0')} className={!isFieldValid.testPartyCommission.isValid && dirtyFields.testPartyCommission ? "border-2 border-red-600" : ""} onChange={handleChange} error={isFieldValid.testPartyCommission.message} />
          </div>
          <div className="min-w-0 flex-1 basis-0">
            <InputField displayLabel={false} onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                e.stopPropagation();
                // Focus on Save & Continue button in add mode, Save Party button in edit mode
                if (!id && saveAndContinueButtonRef.current) {
                  saveAndContinueButtonRef.current.focus();
                } else if (id && savePartyButtonRef.current) {
                  savePartyButtonRef.current.focus();
                }
              } else {
                handleKeyDown(e);
              }
            }} min={-999} max={999} maxLength={8} type='text' label="Test Third Party Commission" name="testThirdPartyCommission" value={commissionInputValues['testThirdPartyCommission'] !== undefined ? commissionInputValues['testThirdPartyCommission'] : (formData.testThirdPartyCommission || '0')} onChange={handleChange} className={`${!isFieldValid.testThirdPartyCommission.isValid && dirtyFields.testThirdPartyCommission ? "border-2 border-red-600" : ""} ${(!thirdPartyValidation.isValid || !thirdPartyValidation.partyId) ? "bg-gray-200" : ""}`} disabled={!thirdPartyValidation.isValid || !thirdPartyValidation.partyId} error={isFieldValid.testThirdPartyCommission.message} />
          </div>
        </div>

        <label
          className="flex items-start gap-2 cursor-pointer select-none pt-1"
          title="Hidden in Trial Balance only; still visible in Ledger and other reports."
        >
          <input
            type="checkbox"
            name="hideInGeneral"
            checked={formData.hideInGeneral ?? false}
            onChange={handleChange}
            className="mt-0.5 h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
          />
          <span className="text-sm font-medium text-gray-700">
            Hide in General
            <span className="block text-xs font-normal text-gray-500">
              Hidden in Trial Balance only; still visible in Ledger and other reports.
            </span>
          </span>
        </label>

        <div className="flex justify-end space-x-2 pt-2 mt-3 border-t border-gray-200">
          {/* Save & Continue button - only visible in add mode */}
          {!id && (
            <Button 
              ref={saveAndContinueButtonRef}
              type="button"
              onClick={handleSaveAndContinue}
              isProcessing={isProcessing}
              className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Save & Continue
            </Button>
          )}
          <Button 
            ref={savePartyButtonRef}
            type="submit"
            isProcessing={isProcessing}
            className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Save Party
          </Button>
          <Button
            type="button"
            onClick={() => onClose(false)}
            className="bg-gray-400 hover:bg-gray-500 text-white px-4 py-2 rounded-md"
          >
            Cancel
          </Button>
        </div>
      </form>
    </SideDrawer>
  );
};

export default CreatePartyDialog;
